import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/apiClient';
import server from '../../utils/serverConfig';
import {
    ChevronDown, ChevronRight, Search, ArrowUp, ArrowDown,
    BarChart2, ChevronLeft, Download, RefreshCw, Loader2, AlertCircle, X, Upload,
    Info, Copy, Check, User, Users, Flag
} from 'lucide-react';
import { useGlobalUsers } from '../../hooks/useGlobalHooks';
import {
    FMT_PCT, FMT_RANGE, FMT_DATETIME_SHORT, getLabelStyles
} from './utils/auditHelpers';
import FamilyConfigModal from './components/FamilyConfigModal';

const PerformanceView = () => {
    const { users } = useGlobalUsers();
    const [view, setView] = useState('monthly');         // 'monthly' | 'weekly' | 'daily'
    const [year, setYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(null);  // 1-12
    const [selectedWeek, setSelectedWeek] = useState(null);  // 1-4
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [collapsedCategories, setCollapsedCategories] = useState({});

    const toggleCategory = (cat) => {
        setCollapsedCategories(prev => ({
            ...prev,
            [cat]: !prev[cat]
        }));
    };

    // Family Configuration & Grouping States
    const [showFamilyModal, setShowFamilyModal] = useState(false);
    const [familyGroups, setFamilyGroups] = useState([]);
    const [modalViewMode, setModalViewMode] = useState('individual'); // 'individual' | 'family'
    const [expandedFamilies, setExpandedFamilies] = useState({});

    const fetchFamilyGroups = useCallback(async () => {
        try {
            const res = await apiClient.get(`${server.godseye.baseUrl}audit-performance/family-groups`);
            setFamilyGroups(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Failed to fetch family groups", err);
        }
    }, []);

    useEffect(() => {
        fetchFamilyGroups();
    }, [fetchFamilyGroups]);

    // Dynamic Client Audits States
    const [selectedCell, setSelectedCell] = useState(null); // { strat, period, date }
    const [clientsList, setClientsList] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null); // client ID
    const [clientAuditData, setClientAuditData] = useState([]);
    const [clientAuditLoading, setClientAuditLoading] = useState(false);

    // CSV Audit Comparison States
    const [uploadedAuditCsv, setUploadedAuditCsv] = useState(null);
    const fileInputRef = React.useRef(null);

    // Client list search & sort states
    const [clientSearchQuery, setClientSearchQuery] = useState('');
    const [selectedBroker, setSelectedBroker] = useState('All');
    const [clientSortConfig, setClientSortConfig] = useState({ key: 'net_pnl_pct', direction: 'desc' });
    const showNetPnl = true; // Net PnL is permanent default

    // Charges Info & Breakdown Popup states
    const [chargesPopup, setChargesPopup] = useState(null); // popup modal on click
    const [copiedState, setCopiedState] = useState(false);

    const handleCopyCharges = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedState(true);
        setTimeout(() => setCopiedState(false), 2000);
    };


    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Parse uploaded CSV text into header array and Acc ID dictionary
    const parseCSVText = (text) => {
        const lines = text.split(/\r\n|\n/);
        if (lines.length === 0) return { headers: [], records: {} };

        const parseLine = (line) => {
            const result = [];
            let start = 0;
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                if (line[i] === '"') {
                    inQuotes = !inQuotes;
                } else if (line[i] === ',' && !inQuotes) {
                    let val = line.substring(start, i).trim();
                    if (val.startsWith('"') && val.endsWith('"')) {
                        val = val.substring(1, val.length - 1).replace(/""/g, '"');
                    }
                    result.push(val);
                    start = i + 1;
                }
            }
            let lastVal = line.substring(start).trim();
            if (lastVal.startsWith('"') && lastVal.endsWith('"')) {
                lastVal = lastVal.substring(1, lastVal.length - 1).replace(/""/g, '"');
            }
            result.push(lastVal);
            return result;
        };

        const headers = parseLine(lines[0]);
        const accIdIdx = headers.findIndex(h => h.trim().toLowerCase() === 'acc id');
        const records = {};

        for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue;
            const cols = parseLine(lines[i]);
            if (accIdIdx !== -1 && cols[accIdIdx]) {
                const cid = cols[accIdIdx].trim().toLowerCase();
                const rowObj = {};
                headers.forEach((h, idx) => {
                    rowObj[h.trim()] = cols[idx] !== undefined ? cols[idx] : '';
                });
                records[cid] = rowObj;
            }
        }
        return { headers, records };
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const parsed = parseCSVText(event.target.result);
            setUploadedAuditCsv({
                fileName: file.name,
                headers: parsed.headers,
                records: parsed.records
            });
        };
        reader.readAsText(file);
    };

    // Target column resolution in CSV: eqColHeader with fallback to monthColHeader per client
    const { eqColHeader, monthColHeader } = React.useMemo(() => {
        if (!uploadedAuditCsv || !selectedCell) return { eqColHeader: null, monthColHeader: null };
        const { headers } = uploadedAuditCsv;

        let eq = null;
        if (showNetPnl) {
            // When in Net PnL mode, first look for Net PnL specific column
            eq = headers.find(h => {
                const hLower = h.trim().toLowerCase();
                return hLower === 'net pnl' || hLower === 'net p&l' || hLower === 'net_pnl' || hLower === 'net profit' || hLower === 'net amount';
            }) || null;
        }

        if (!eq) {
            eq = headers.find(h => {
                const hLower = h.trim().toLowerCase();
                return hLower === 'eq pnl' || hLower === 'eq p&l' || hLower === 'eq_pnl' || hLower === 'eq value' ||
                       hLower === 'net pnl' || hLower === 'net p&l' || hLower === 'net_pnl' ||
                       hLower === 'pnl' || hLower === 'p&l' || hLower === 'realized pnl' || hLower === 'realised pnl';
            }) || null;
        }

        const monthMap = {
            1: ['jan', 'january'],
            2: ['feb', 'february'],
            3: ['mar', 'march'],
            4: ['apr', 'april'],
            5: ['may'],
            6: ['jun', 'june'],
            7: ['jul', 'july'],
            8: ['aug', 'august'],
            9: ['sep', 'sept', 'september'],
            10: ['oct', 'october'],
            11: ['nov', 'november'],
            12: ['dec', 'december']
        };

        let targetMonthNames = [];
        if (selectedCell.month) {
            targetMonthNames = monthMap[selectedCell.month] || [];
        } else if (selectedCell.period) {
            const pLower = selectedCell.period.toLowerCase();
            targetMonthNames = [pLower];
            if (pLower.startsWith('jan')) targetMonthNames.push('jan', 'january');
            if (pLower.startsWith('feb')) targetMonthNames.push('feb', 'february');
            if (pLower.startsWith('mar')) targetMonthNames.push('mar', 'march');
            if (pLower.startsWith('apr')) targetMonthNames.push('apr', 'april');
            if (pLower.startsWith('may')) targetMonthNames.push('may');
            if (pLower.startsWith('jun')) targetMonthNames.push('jun', 'june');
            if (pLower.startsWith('jul')) targetMonthNames.push('jul', 'july');
            if (pLower.startsWith('aug')) targetMonthNames.push('aug', 'august');
            if (pLower.startsWith('sep')) targetMonthNames.push('sep', 'sept', 'september');
            if (pLower.startsWith('oct')) targetMonthNames.push('oct', 'october');
            if (pLower.startsWith('nov')) targetMonthNames.push('nov', 'november');
            if (pLower.startsWith('dec')) targetMonthNames.push('dec', 'december');
        }

        const yrStr = String(selectedCell.year || new Date().getFullYear()).slice(-2);

        let monthCol = null;
        for (const h of headers) {
            const hLower = h.toLowerCase().trim();
            for (const mName of targetMonthNames) {
                if (
                    hLower === `${mName}-${yrStr}` ||
                    hLower === `${mName}${yrStr}` ||
                    hLower.includes(`${mName}-${yrStr}`) ||
                    hLower.includes(`${mName} ${yrStr}`) ||
                    (hLower.includes(mName) && hLower.includes(yrStr))
                ) {
                    monthCol = h;
                    break;
                }
            }
            if (monthCol) break;
        }

        return { eqColHeader: eq, monthColHeader: monthCol };
    }, [uploadedAuditCsv, selectedCell, showNetPnl]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let url;
            if (view === 'monthly') {
                url = `${server.godseye.baseUrl}audit-performance/monthly?year=${year}`;
            } else if (view === 'weekly') {
                url = `${server.godseye.baseUrl}audit-performance/weekly?year=${year}&month=${selectedMonth}`;
            } else {
                url = `${server.godseye.baseUrl}audit-performance/daily?year=${year}&month=${selectedMonth}&week=${selectedWeek}`;
            }
            const res = await apiClient.get(url);
            setData(res.data);
        } catch (e) {
            setError('Failed to fetch performance data. ' + (e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }, [view, year, selectedMonth, selectedWeek]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Drill-down handlers
    const drillToWeekly = (monthIdx) => {
        setSelectedMonth(monthIdx + 1);
        setView('weekly');
    };
    const drillToDaily = (weekNum) => {
        setSelectedWeek(weekNum);
        setView('daily');
    };
    const goBack = () => {
        if (view === 'daily') { setView('weekly'); setSelectedWeek(null); }
        else if (view === 'weekly') { setView('monthly'); setSelectedMonth(null); }
    };

    const handleCellClick = async (strat, period, label = null) => {
        setSelectedClient(null);
        setClientsList([]);
        setClientsLoading(true);
        setClientSearchQuery('');
        setSelectedBroker('All');

        try {
            let url = '';
            let cellInfo = { strat, period, view, label };

            const labelParam = label ? `&label=${encodeURIComponent(label)}` : '';

            if (view === 'monthly') {
                const monthNum = MONTH_NAMES.indexOf(period) + 1;
                url = `${server.godseye.baseUrl}audit-performance/monthly-clients?year=${year}&month=${monthNum}&asset=${encodeURIComponent(strat)}${labelParam}`;
                cellInfo = { ...cellInfo, year, month: monthNum };
            } else if (view === 'weekly') {
                const weekNum = parseInt(period.replace('W', ''), 10);
                url = `${server.godseye.baseUrl}audit-performance/weekly-clients?year=${year}&month=${selectedMonth}&week=${weekNum}&asset=${encodeURIComponent(strat)}${labelParam}`;
                cellInfo = { ...cellInfo, year, month: selectedMonth, week: weekNum };
            } else {
                // daily view
                const date = data?.period_dates?.[period];
                if (!date) {
                    setClientsLoading(false);
                    return;
                }
                url = `${server.godseye.baseUrl}audit-performance/daily-clients?date=${date}&asset=${encodeURIComponent(strat)}${labelParam}`;
                cellInfo = { ...cellInfo, date };
            }

            setSelectedCell(cellInfo);
            const res = await apiClient.get(url);
            setClientsList(res.data);
        } catch (e) {
            console.error("Failed to fetch clients list", e);
        } finally {
            setClientsLoading(false);
        }
    };

    const handleClientClick = async (client) => {
        setSelectedClient(client);
        setClientAuditData([]);
        setClientAuditLoading(true);

        try {
            let url = `${server.godseye.baseUrl}audit-performance/client-audit?client_id=${client.client_id}&asset=${encodeURIComponent(selectedCell.strat)}`;
            if (selectedCell.view === 'monthly') {
                url += `&year=${selectedCell.year}&month=${selectedCell.month}`;
            } else if (selectedCell.view === 'weekly') {
                url += `&year=${selectedCell.year}&month=${selectedCell.month}&week=${selectedCell.week}`;
            } else {
                url += `&date=${selectedCell.date}`;
            }

            const res = await apiClient.get(url);
            setClientAuditData(res.data);
        } catch (e) {
            console.error("Failed to fetch client audit data", e);
        } finally {
            setClientAuditLoading(false);
        }
    };

    const handleClientSort = (key) => {
        let direction = 'asc';
        if (clientSortConfig.key === key && clientSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setClientSortConfig({ key, direction });
    };

    // Quick Map client_id -> family metadata
    const clientFamilyMap = React.useMemo(() => {
        const map = {};
        (familyGroups || []).forEach(fg => {
            (fg.accounts || []).forEach(acc => {
                map[acc.client_id.trim().toLowerCase()] = {
                    family_name: fg.family_name,
                    color: fg.color || '#3B82F6',
                    flag: !!acc.flag,
                    notes: acc.notes || ''
                };
            });
        });
        return map;
    }, [familyGroups]);

    // Quick toggle for account boolean flag
    const handleQuickToggleFlag = async (clientId, currentFlag) => {
        try {
            const newFlag = !currentFlag;
            // Optimistic update
            setFamilyGroups(prev => prev.map(g => ({
                ...g,
                accounts: (g.accounts || []).map(a =>
                    a.client_id.toLowerCase() === clientId.toLowerCase()
                        ? { ...a, flag: newFlag }
                        : a
                )
            })));

            await apiClient.put(`${server.godseye.baseUrl}audit-performance/family-account-flag`, {
                client_id: clientId,
                flag: newFlag
            });
        } catch (e) {
            console.error("Failed to toggle flag", e);
            fetchFamilyGroups();
        }
    };

    const handleDownloadCSV = () => {
        if (!sortedClients || sortedClients.length === 0) return;

        // 1. Define headers
        const headers = [
            "Client ID",
            "Family Group",
            "Flagged",
            "Name",
            "Label",
            "Trades Count",
            "Multiplier",
            "Deployed Capital",
            "Available Funds",
            "Delta Funds",
            "Ratio",
            "Funds Updated At",
            "Gross PnL",
            "Charges",
            "Net PnL",
            "Net PnL %",
            ...(uploadedAuditCsv ? [
                "Broker PnL",
                "Broker PnL %",
                "Audit Diff (INR)",
                "Audit Diff (%)"
            ] : []),
            "Download Date"
        ];
        const csvRows = [headers.join(",")];

        // 2. Add data rows
        sortedClients.forEach(c => {
            const values = [
                `"${c.client_id || ''}"`,
                `"${(c.family_name || 'Unassigned').replace(/"/g, '""')}"`,
                c.flag ? "YES" : "NO",
                `"${(c.name || '').replace(/"/g, '""')}"`,
                `"${c.label || ''}"`,
                c.trades_count !== undefined ? c.trades_count : 0,
                c.multiplier !== undefined ? c.multiplier : '-',
                c.deployed_capital !== undefined ? c.deployed_capital : 0,
                c.available_funds !== undefined ? c.available_funds : 0,
                c.delta_funds !== undefined ? c.delta_funds : 0,
                c.ratio !== undefined && !isNaN(c.ratio) && isFinite(c.ratio) ? Number(c.ratio.toFixed(2)) : '-',
                c.balance_updated_at ? `"${c.balance_updated_at}"` : '"-"',
                c.total_pnl !== undefined ? c.total_pnl.toFixed(2) : 0,
                c.total_charges !== undefined ? c.total_charges.toFixed(2) : 0,
                c.net_pnl !== undefined ? c.net_pnl.toFixed(2) : 0,
                c.net_pnl_pct !== undefined ? `${c.net_pnl_pct.toFixed(2)}%` : '0%',
                ...(uploadedAuditCsv ? [
                    c.manual_pnl !== undefined ? c.manual_pnl.toFixed(2) : '-',
                    c.manual_pnl_pct !== undefined ? `${c.manual_pnl_pct.toFixed(2)}%` : '-',
                    c.delta_pnl !== undefined ? c.delta_pnl.toFixed(2) : '-',
                    c.delta_pnl_pct !== undefined ? `${c.delta_pnl_pct.toFixed(2)}%` : '-',
                ] : []),
                `"${new Date().toLocaleDateString('en-GB')}"`
            ];
            csvRows.push(values.join(","));
        });

        const csvContent = csvRows.join("\n");

        // 3. Create Blob and Link
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        // 4. Set filename based on strategy and period
        let periodName = selectedCell.period;
        if (selectedCell.view === 'monthly') {
            periodName = `${selectedCell.period}_${selectedCell.year}`;
        } else if (selectedCell.view === 'weekly') {
            periodName = `${selectedCell.period}_${selectedCell.year}`;
        } else {
            periodName = selectedCell.date || 'daily';
        }
        const safeStratName = selectedCell.strat.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const safePeriodName = periodName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const filename = `${safeStratName}_${safePeriodName}_clients.csv`;

        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);

        // 5. Trigger Download and Cleanup
        link.click();
        document.body.removeChild(link);
    };

    const enrichedClients = React.useMemo(() => {
        if (!clientsList) return [];
        return clientsList.map(c => {
            const cidLower = (c.client_id || "").toString().trim().toLowerCase();
            const fMeta = clientFamilyMap[cidLower];
            let baseClient = c;
            if (c.client_id === 'SGX_JOURNAL') {
                baseClient = {
                    ...c,
                    name: 'SGX',
                    label: '-',
                    broker: 'GoogleSheet',
                    available_funds: 1500000.0,
                    delta_funds: 0.0,
                    ratio: (c.deployed_capital !== undefined && c.deployed_capital > 0) ? (c.deployed_capital / 1500000.0) : 1.0,
                    balance_updated_at: undefined,
                    family_name: fMeta ? fMeta.family_name : 'Unassigned Accounts',
                    family_color: fMeta ? fMeta.color : '#6B7280',
                    flag: fMeta ? fMeta.flag : false,
                    family_notes: fMeta ? fMeta.notes : ''
                };
            } else {
                const user = users?.find(u =>
                    (u.client_id || u.clientId || "").toString().trim().toLowerCase() === cidLower
                );
                const rawBalance = user?.balance !== undefined && user?.balance !== null ? Number(user.balance) : undefined;
                const available_funds = (rawBalance !== undefined && !isNaN(rawBalance)) ? rawBalance : undefined;
                const balance_updated_at = user?.balance_updated_at || undefined;
                const deployed_capital = c.deployed_capital;
                const delta_funds = (deployed_capital !== undefined && available_funds !== undefined)
                    ? (deployed_capital - available_funds)
                    : undefined;
                const ratio = (deployed_capital !== undefined && available_funds !== undefined && available_funds !== 0)
                    ? (deployed_capital / available_funds)
                    : undefined;

                let mult = c.multiplier;
                if (mult === undefined || mult === null || isNaN(Number(mult))) {
                    if (deployed_capital !== undefined && deployed_capital > 0) {
                        mult = Number((deployed_capital / 1500000.0).toFixed(2));
                    } else if (user?.multiplier !== undefined && user?.multiplier !== null && !isNaN(Number(user.multiplier))) {
                        mult = Number(user.multiplier);
                    } else {
                        mult = 1;
                    }
                } else {
                    mult = Number(mult);
                }

                baseClient = {
                    ...c,
                    name: user?.name || '-',
                    label: user?.label || '-',
                    broker: c.broker || 'Unknown',
                    available_funds,
                    delta_funds,
                    ratio,
                    balance_updated_at,
                    multiplier: mult,
                    family_name: fMeta ? fMeta.family_name : 'Unassigned Accounts',
                    family_color: fMeta ? fMeta.color : '#6B7280',
                    flag: fMeta ? fMeta.flag : false,
                    family_notes: fMeta ? fMeta.notes : ''
                };
            }

            let manual_pnl = undefined;
            let manual_pnl_pct = undefined;
            let delta_pnl = undefined;
            let delta_pnl_pct = undefined;

            if (uploadedAuditCsv && uploadedAuditCsv.records) {
                const csvRecord = uploadedAuditCsv.records[cidLower];
                if (csvRecord) {
                    let rawStr = null;
                    // First try EQ PNL for this client
                    if (eqColHeader && csvRecord[eqColHeader] !== undefined && csvRecord[eqColHeader] !== '' && csvRecord[eqColHeader] !== null) {
                        const testVal = csvRecord[eqColHeader].toString().replace(/,/g, '').replace(/"/g, '').trim();
                        if (testVal && testVal !== '-') rawStr = testVal;
                    }
                    // If EQ PNL is not available for this specific client, fallback to month column (e.g. August-26)
                    if (!rawStr && monthColHeader && csvRecord[monthColHeader] !== undefined && csvRecord[monthColHeader] !== '' && csvRecord[monthColHeader] !== null) {
                        const testVal = csvRecord[monthColHeader].toString().replace(/,/g, '').replace(/"/g, '').trim();
                        if (testVal && testVal !== '-') rawStr = testVal;
                    }

                    if (rawStr) {
                        const parsedVal = parseFloat(rawStr);
                        if (!isNaN(parsedVal)) {
                            manual_pnl = parsedVal;
                            const deployed = baseClient.deployed_capital || 0;
                            manual_pnl_pct = deployed > 0 ? (manual_pnl / deployed) * 100 : 0;

                            // When showNetPnl is active, calculate difference from net_pnl and net_pnl_pct instead of total_pnl
                            const comparePnl = (showNetPnl && baseClient.net_pnl !== undefined && baseClient.net_pnl !== null)
                                ? baseClient.net_pnl
                                : (baseClient.total_pnl || 0);
                            const comparePnlPct = (showNetPnl && baseClient.net_pnl_pct !== undefined && baseClient.net_pnl_pct !== null)
                                ? baseClient.net_pnl_pct
                                : (baseClient.pnl_pct || 0);

                            delta_pnl = comparePnl - manual_pnl;
                            delta_pnl_pct = comparePnlPct - manual_pnl_pct;
                        }
                    }
                }
            }

            return {
                ...baseClient,
                manual_pnl,
                manual_pnl_pct,
                delta_pnl,
                delta_pnl_pct
            };
        });
    }, [clientsList, users, uploadedAuditCsv, eqColHeader, monthColHeader, showNetPnl, clientFamilyMap]);

    const { minClient, maxClient } = React.useMemo(() => {
        if (!enrichedClients || enrichedClients.length === 0) return { minClient: null, maxClient: null };
        const realClients = enrichedClients.filter(c => c.client_id !== 'CA00246');
        if (realClients.length === 0) return { minClient: null, maxClient: null };
        let min = realClients[0];
        let max = realClients[0];
        for (let i = 1; i < realClients.length; i++) {
            if (realClients[i].pnl_pct < min.pnl_pct) min = realClients[i];
            if (realClients[i].pnl_pct > max.pnl_pct) max = realClients[i];
        }
        return {
            minClient: min,
            maxClient: min.client_id !== max.client_id ? max : null
        };
    }, [enrichedClients]);

    const sortedClients = React.useMemo(() => {
        let result = enrichedClients.filter(c => 
            (selectedBroker === 'All' || c.broker === selectedBroker) &&
            (c.client_id.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
             c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
             (c.family_name && c.family_name.toLowerCase().includes(clientSearchQuery.toLowerCase())) ||
             c.label.toLowerCase().includes(clientSearchQuery.toLowerCase()))
        );
        const { key, direction } = clientSortConfig;
        if (key) {
            result.sort((a, b) => {
                let aVal = a[key];
                let bVal = b[key];
                
                if (aVal === bVal) return 0;
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;
                
                if (typeof aVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                }
                
                return direction === 'asc'
                    ? (aVal > bVal ? 1 : -1)
                    : (aVal < bVal ? 1 : -1);
            });
        }
        return result;
    }, [enrichedClients, clientSearchQuery, clientSortConfig, selectedBroker]);

    // Grouping for Family View
    const familyGroupedData = React.useMemo(() => {
        if (!sortedClients || sortedClients.length === 0) return [];

        const groupsMap = {};

        sortedClients.forEach(c => {
            const fname = c.family_name || 'Unassigned Accounts';
            const fColor = c.family_color || '#6B7280';
            const isUnassigned = fname === 'Unassigned Accounts';

            if (!groupsMap[fname]) {
                groupsMap[fname] = {
                    family_name: fname,
                    color: fColor,
                    isUnassigned,
                    clients: [],
                    totals: {
                        trades_count: 0,
                        multiplier: 0,
                        deployed_capital: 0,
                        available_funds: 0,
                        delta_funds: 0,
                        ratio: 0,
                        latest_balance_updated_at: null,
                        total_pnl: 0,
                        total_charges: 0,
                        net_pnl: 0,
                        net_pnl_pct: 0,
                        manual_pnl: 0,
                        hasManualPnl: false,
                        manual_pnl_pct: 0,
                        delta_pnl: 0,
                        delta_pnl_pct: 0,
                        flagged_count: 0
                    }
                };
            }

            groupsMap[fname].clients.push(c);
            const t = groupsMap[fname].totals;

            if (c.trades_count !== undefined) t.trades_count += c.trades_count;
            if (c.multiplier !== undefined && c.multiplier !== null && !isNaN(Number(c.multiplier))) {
                t.multiplier += Number(c.multiplier);
            }
            if (c.deployed_capital !== undefined) t.deployed_capital += c.deployed_capital;
            if (c.available_funds !== undefined) t.available_funds += c.available_funds;
            if (c.delta_funds !== undefined) t.delta_funds += c.delta_funds;
            if (c.balance_updated_at) {
                if (!t.latest_balance_updated_at || new Date(c.balance_updated_at.replace(' ', 'T')) > new Date(t.latest_balance_updated_at.replace(' ', 'T'))) {
                    t.latest_balance_updated_at = c.balance_updated_at;
                }
            }
            if (c.total_pnl !== undefined) t.total_pnl += c.total_pnl;
            if (c.total_charges !== undefined) t.total_charges += c.total_charges;
            if (c.net_pnl !== undefined) t.net_pnl += c.net_pnl;
            if (c.manual_pnl !== undefined) {
                t.manual_pnl += c.manual_pnl;
                t.hasManualPnl = true;
            }
            if (c.flag) t.flagged_count++;
        });

        const result = Object.values(groupsMap).map(g => {
            const t = g.totals;
            t.ratio = (t.available_funds !== undefined && t.available_funds !== 0)
                ? (t.deployed_capital / t.available_funds)
                : 0;
            t.net_pnl_pct = t.deployed_capital > 0 ? (t.net_pnl / t.deployed_capital) * 100 : 0;
            if (t.hasManualPnl) {
                t.manual_pnl_pct = t.deployed_capital > 0 ? (t.manual_pnl / t.deployed_capital) * 100 : 0;
                t.delta_pnl = t.net_pnl - t.manual_pnl;
                t.delta_pnl_pct = t.net_pnl_pct - t.manual_pnl_pct;
            }
            return g;
        });

        // Sort so named families come first, Unassigned at the end
        result.sort((a, b) => {
            if (a.isUnassigned && !b.isUnassigned) return 1;
            if (!a.isUnassigned && b.isUnassigned) return -1;
            return a.family_name.localeCompare(b.family_name);
        });

        return result;
    }, [sortedClients]);

    // Accordion toggle helpers
    const toggleFamilyAccordion = (familyName) => {
        setExpandedFamilies(prev => ({
            ...prev,
            [familyName]: prev[familyName] === undefined ? false : !prev[familyName]
        }));
    };

    const isFamilyExpanded = (familyName) => {
        return expandedFamilies[familyName] !== false; // default open
    };

    const toggleAllFamilies = (expand) => {
        const newState = {};
        familyGroupedData.forEach(g => {
            newState[g.family_name] = expand;
        });
        setExpandedFamilies(newState);
    };

    const clientTotals = React.useMemo(() => {
        if (!sortedClients || sortedClients.length === 0) return null;
        let totalTrades = 0;
        let totalMultiplier = 0;
        let deployed = 0;
        let avail = 0;
        let deltaFunds = 0;
        let grossPnl = 0;
        let charges = 0;
        let netPnl = 0;
        let brokerPnl = 0;
        let hasBroker = false;

        let totalBrokerage = 0;
        let totalStt = 0;
        let totalExchange = 0;
        let totalSebi = 0;
        let totalStamp = 0;
        let totalGst = 0;

        let latestBalanceUpdated = null;

        sortedClients.forEach(c => {
            if (c.trades_count !== undefined) totalTrades += c.trades_count;
            if (c.multiplier !== undefined && c.multiplier !== null && !isNaN(Number(c.multiplier))) {
                totalMultiplier += Number(c.multiplier);
            }
            if (c.deployed_capital !== undefined) deployed += c.deployed_capital;
            if (c.available_funds !== undefined) avail += c.available_funds;
            if (c.delta_funds !== undefined) deltaFunds += c.delta_funds;
            if (c.balance_updated_at) {
                if (!latestBalanceUpdated || new Date(c.balance_updated_at.replace(' ', 'T')) > new Date(latestBalanceUpdated.replace(' ', 'T'))) {
                    latestBalanceUpdated = c.balance_updated_at;
                }
            }
            if (c.total_pnl !== undefined) grossPnl += c.total_pnl;
            if (c.total_charges !== undefined) charges += c.total_charges;
            if (c.net_pnl !== undefined) netPnl += c.net_pnl;
            if (c.manual_pnl !== undefined) {
                brokerPnl += c.manual_pnl;
                hasBroker = true;
            }
            if (c.charges_breakdown) {
                totalBrokerage += c.charges_breakdown.brokerage || 0;
                totalStt += c.charges_breakdown.stt || 0;
                totalExchange += c.charges_breakdown.exchange_charges || 0;
                totalSebi += c.charges_breakdown.sebi_fee || 0;
                totalStamp += c.charges_breakdown.stamp_duty || 0;
                totalGst += c.charges_breakdown.gst || 0;
            }
        });

        const ratio = (avail !== 0) ? (deployed / avail) : 0;
        const grossPct = deployed > 0 ? (grossPnl / deployed) * 100 : 0;
        const netPct = deployed > 0 ? (netPnl / deployed) * 100 : 0;
        const brokerPct = (deployed > 0 && hasBroker) ? (brokerPnl / deployed) * 100 : 0;
        const diffPnl = (showNetPnl ? netPnl : grossPnl) - (hasBroker ? brokerPnl : 0);
        const diffPct = (showNetPnl ? netPct : grossPct) - (hasBroker ? brokerPct : 0);

        return {
            count: sortedClients.length,
            totalTrades,
            totalMultiplier,
            deployed,
            avail,
            deltaFunds,
            ratio,
            latestBalanceUpdated,
            grossPnl,
            charges,
            netPnl,
            grossPct,
            netPct,
            brokerPnl: hasBroker ? brokerPnl : undefined,
            brokerPct: hasBroker ? brokerPct : undefined,
            diffPnl: hasBroker ? diffPnl : undefined,
            diffPct: hasBroker ? diffPct : undefined,
            totalBrokerage,
            totalStt,
            totalExchange,
            totalSebi,
            totalStamp,
            totalGst
        };
    }, [sortedClients, showNetPnl]);

    const generateChargesCopyText = (data) => {
        if (!data) return '';
        if (data.type === 'rates') {
            return [
                "=== FYERS F&O STATUTORY & BROKERAGE CHARGES LIST ===",
                "1. Brokerage: Flat ₹20 per executed order (₹40 per round-trip trade)",
                "2. STT / CTT:",
                "   • Options: 0.15% on sell-side premium turnover",
                "   • Futures: 0.05% on sell-side turnover",
                "3. Transaction Charges (Exchange + Clearing):",
                "   • NSE Options: 0.0355299% (Exch) + 0.009% (Clearing) = 0.0445299% on premium turnover",
                "   • BSE Options: 0.0325% (Exch) + 0.009% (Clearing) = 0.0415% on premium turnover",
                "   • NSE Futures: 0.0018299% (Exch) + 0.0005% (Clearing) = 0.0023299% on turnover",
                "4. SEBI Turnover Fee: ₹10 per Crore (0.000001) on total turnover",
                "5. Stamp Duty:",
                "   • Options: 0.003% on buy-side premium turnover",
                "   • Futures: 0.002% on buy-side turnover",
                "6. GST: 18% on (Brokerage + Exchange & Clearing Charges + SEBI Fee)",
                "----------------------------------------------------",
                "Reference: https://fyers.in/charges-list"
            ].join("\n");
        }

        if (data.type === 'client') {
            const c = data.client;
            const b = c.charges_breakdown || {};
            return [
                `=== CHARGES BREAKDOWN — CLIENT ${c.client_id} ===`,
                `Name: ${c.name || '-'}`,
                `Broker: ${c.broker || 'Unknown'}`,
                `Trades Count: ${c.trades_count || 0}`,
                `Gross PnL: ₹${c.total_pnl !== undefined ? c.total_pnl.toFixed(2) : '0.00'}`,
                `----------------------------------------------------`,
                `• Brokerage (₹20/order):   ₹${(b.brokerage || 0).toFixed(2)}`,
                `• STT / CTT:               ₹${(b.stt || 0).toFixed(2)}`,
                `• Exchange & Clearing:     ₹${(b.exchange_charges || 0).toFixed(2)}`,
                `• SEBI Turnover Fee:       ₹${(b.sebi_fee || 0).toFixed(2)}`,
                `• Stamp Duty:              ₹${(b.stamp_duty || 0).toFixed(2)}`,
                `• GST (18%):               ₹${(b.gst || 0).toFixed(2)}`,
                `----------------------------------------------------`,
                `TOTAL CHARGES:             ₹${(c.total_charges !== undefined ? c.total_charges : b.total_charges || 0).toFixed(2)}`,
                `NET PnL:                   ₹${c.net_pnl !== undefined ? c.net_pnl.toFixed(2) : '0.00'} (${c.net_pnl_pct !== undefined ? c.net_pnl_pct.toFixed(2) : '0.00'}%)`
            ].join("\n");
        }

        if (data.type === 'trade') {
            const t = data.trade;
            const b = t.charges || {};
            return [
                `=== CHARGES BREAKDOWN — TRADE #${data.index || 1} ===`,
                `Symbol: ${t.symbol_l || '-'}`,
                `Qty: ${t.qty_l || '-'}`,
                `Buy: ₹${t.buy_price_l !== null ? Number(t.buy_price_l).toFixed(2) : '-'} | Sell: ₹${t.sell_price_l !== null ? Number(t.sell_price_l).toFixed(2) : '-'}`,
                `----------------------------------------------------`,
                `• Brokerage:               ₹${(b.brokerage || 0).toFixed(2)}`,
                `• STT / CTT:               ₹${(b.stt || 0).toFixed(2)}`,
                `• Exchange & Clearing:     ₹${(b.exchange_charges || 0).toFixed(2)}`,
                `• SEBI Turnover Fee:       ₹${(b.sebi_fee || 0).toFixed(2)}`,
                `• Stamp Duty:              ₹${(b.stamp_duty || 0).toFixed(2)}`,
                `• GST (18%):               ₹${(b.gst || 0).toFixed(2)}`,
                `----------------------------------------------------`,
                `TOTAL CHARGES:             ₹${(b.total_charges || 0).toFixed(2)}`,
                `NET PnL:                   ₹${t.net_pnl_l !== null && t.net_pnl_l !== undefined ? Number(t.net_pnl_l).toFixed(2) : '-'}`
            ].join("\n");
        }

        if (data.type === 'totals') {
            const t = data.totals;
            return [
                `=== TOTAL CHARGES BREAKDOWN — STRATEGY SUMMARY ===`,
                `Clients Count: ${t.count}`,
                `Total Deployed: ₹${t.deployed.toLocaleString('en-IN')}`,
                `Gross PnL: ₹${t.grossPnl.toFixed(2)} (${t.grossPct.toFixed(2)}%)`,
                `----------------------------------------------------`,
                `• Brokerage:               ₹${(t.totalBrokerage || 0).toFixed(2)}`,
                `• STT / CTT:               ₹${(t.totalStt || 0).toFixed(2)}`,
                `• Exchange & Clearing:     ₹${(t.totalExchange || 0).toFixed(2)}`,
                `• SEBI Turnover Fee:       ₹${(t.totalSebi || 0).toFixed(2)}`,
                `• Stamp Duty:              ₹${(t.totalStamp || 0).toFixed(2)}`,
                `• GST (18%):               ₹${(t.totalGst || 0).toFixed(2)}`,
                `----------------------------------------------------`,
                `TOTAL CHARGES:             ₹${t.charges.toFixed(2)}`,
                `NET PnL:                   ₹${t.netPnl.toFixed(2)} (${t.netPct.toFixed(2)}%)`
            ].join("\n");
        }
        return '';
    };


    const periods = data?.periods || [];
    const strategies = data?.strategies || [];
    const tableData = data?.data || {};

    // Period column headers - clickable for drill-down
    const renderPeriodHeader = (period, idx) => {
        const isClickable = view !== 'daily';
        return (
            <th key={period} colSpan={3}
                className={`px-1 py-2 text-center text-xs font-bold uppercase tracking-wider
                    border-l border-[#1F2937]
                    ${isClickable ? 'cursor-pointer hover:bg-blue-900/20 text-blue-300' : 'text-gray-400 cursor-default'}`}
                onClick={() => {
                    if (view === 'monthly') drillToWeekly(idx);
                    else if (view === 'weekly') drillToDaily(idx + 1);
                }}
            >
                <div className="flex items-center justify-center gap-1">
                    {period}
                    {isClickable && <ChevronDown size={11} className="opacity-50" />}
                </div>
            </th>
        );
    };

    const renderSubHeaders = () => periods.map(p => (
        <React.Fragment key={p}>
            <th className="px-2 py-1.5 text-[10px] font-bold text-purple-400 border-l border-[#1F2937] text-center whitespace-nowrap">Backtest</th>
            <th className="px-2 py-1.5 text-[10px] font-bold text-blue-400 text-center whitespace-nowrap">Live</th>
            <th className="px-2 py-1.5 text-[10px] font-bold text-orange-300 text-center whitespace-nowrap">CR</th>
        </React.Fragment>
    ));

    const renderRow = (strat) => {
        const row = tableData[strat] || {};
        const labelsData = row._labels || {};
        const hasLabels = Object.keys(labelsData).length > 0;
        const isExpanded = !!expandedRows[strat];

        let totalBt = 0;
        let totalLive = 0;
        let totalCrMin = null;
        let totalCrMax = null;

        if (view === 'monthly') {
            periods.forEach(p => {
                const cell = row[p] || {};
                if (cell.backtest_pct) totalBt += Number(cell.backtest_pct);
                if (cell.live_pct) totalLive += Number(cell.live_pct);
                if (cell.cr_min !== null && cell.cr_min !== undefined) {
                    totalCrMin = (totalCrMin === null) ? Number(cell.cr_min) : totalCrMin + Number(cell.cr_min);
                }
                if (cell.cr_max !== null && cell.cr_max !== undefined) {
                    totalCrMax = (totalCrMax === null) ? Number(cell.cr_max) : totalCrMax + Number(cell.cr_max);
                }
            });
        }


        return (
            <React.Fragment key={strat}>
                <tr className="hover:bg-[#202330] transition-colors border-b border-gray-800/40">
                    <td className="px-4 py-2.5 text-sm font-semibold text-gray-200 sticky left-0 bg-[#1a1d29] border-r border-gray-800 whitespace-nowrap">
                        <div className="flex flex-wrap items-center gap-2.5 py-1">
                            {hasLabels ? (
                                <button onClick={() => setExpandedRows(prev => ({ ...prev, [strat]: !prev[strat] }))} className="text-gray-400 hover:text-white transition-colors">
                                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </button>
                            ) : (
                                <span className="w-3.5 inline-block"></span>
                            )}
                            {strat}
                        </div>
                    </td>
                    {periods.map(p => {
                        const cell = row[p] || {};
                        const cellClass = `px-2 py-2 text-center text-xs cursor-pointer hover:bg-blue-900/15`;
                        const borderCellClass = `px-2 py-2 text-center border-l border-[#1F2937]/50 text-xs cursor-pointer hover:bg-blue-900/15`;
                        return (
                            <React.Fragment key={p}>
                                <td className={borderCellClass} onClick={() => handleCellClick(strat, p)}>
                                    {FMT_PCT(cell.backtest_pct)}
                                </td>
                                <td className={cellClass} onClick={() => handleCellClick(strat, p)}>
                                    {FMT_PCT(cell.live_pct)}
                                </td>
                                <td className={cellClass} onClick={() => handleCellClick(strat, p)}>
                                    {FMT_RANGE(cell.cr_min, cell.cr_max)}
                                </td>
                            </React.Fragment>
                        );
                    })}
                    {view === 'monthly' && (
                        <td className="px-4 py-2 text-center sticky right-0 bg-[#1f263b] border-l-2 border-blue-500/40 shadow-[-6px_0_15px_rgba(0,0,0,0.4)] z-20">
                            <div className="flex items-center justify-center gap-4">
                                <div className="w-14 text-center">{FMT_PCT(totalBt)}</div>
                                <div className="w-14 text-center">{FMT_PCT(totalLive)}</div>
                                <div className="w-24 text-center">{FMT_RANGE(totalCrMin, totalCrMax)}</div>
                            </div>
                        </td>
                    )}
                </tr>
                
                {isExpanded && hasLabels && Object.keys(labelsData).map(label => {
                    let lTotalMode = 0;
                    let lTotalCrMin = null;
                    let lTotalCrMax = null;
                    if (view === 'monthly') {
                        periods.forEach(p => {
                            const lblCell = labelsData[label]?.[p] || {};
                            if (lblCell.mode_pct) lTotalMode += Number(lblCell.mode_pct);
                            if (lblCell.cr_min !== null && lblCell.cr_min !== undefined) {
                                lTotalCrMin = (lTotalCrMin === null) ? Number(lblCell.cr_min) : lTotalCrMin + Number(lblCell.cr_min);
                            }
                            if (lblCell.cr_max !== null && lblCell.cr_max !== undefined) {
                                lTotalCrMax = (lTotalCrMax === null) ? Number(lblCell.cr_max) : lTotalCrMax + Number(lblCell.cr_max);
                            }
                        });
                    }

                    return (
                    <tr key={`${strat}-${label}`} className="bg-[#14161f] hover:bg-[#202330] transition-colors border-b border-gray-800/40">
                        <td className="px-4 py-2 pl-10 text-xs font-semibold text-gray-400 sticky left-0 bg-[#14161f] border-r border-gray-800 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                                <span className="text-gray-600 font-mono text-[11px] mr-1">{'\u21B3'}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] ${getLabelStyles(label)}`}>{label}</span>
                            </div>
                        </td>
                        {periods.map(p => {
                            const lblCell = labelsData[label]?.[p] || {};
                            const hasCrData = lblCell.cr_min !== undefined || lblCell.mode_pct !== undefined;
                            return (
                                <React.Fragment key={p}>
                                    <td className="px-2 py-1.5 text-center border-l border-[#1F2937]/50 text-xs text-gray-500">
                                        -
                                    </td>
                                    <td
                                        className={`px-2 py-1.5 text-center text-xs text-gray-400 ${hasCrData ? 'cursor-pointer hover:bg-blue-500/10' : ''}`}
                                        title={hasCrData ? `View ${label} clients` : ''}
                                        onClick={() => hasCrData && handleCellClick(strat, p, label)}
                                    >
                                        {FMT_PCT(lblCell.mode_pct)}
                                    </td>
                                    <td
                                        className={`px-2 py-1.5 text-center text-xs text-gray-400 ${hasCrData ? 'cursor-pointer hover:bg-blue-500/10' : ''}`}
                                        title={hasCrData ? `View ${label} clients` : ''}
                                        onClick={() => hasCrData && handleCellClick(strat, p, label)}
                                    >
                                        {FMT_RANGE(lblCell.cr_min, lblCell.cr_max)}
                                    </td>
                                </React.Fragment>
                            );
                        })}
                        {view === 'monthly' && (
                            <td className="px-4 py-1.5 text-center sticky right-0 bg-[#1e2336] border-l-2 border-blue-500/40 shadow-[-6px_0_15px_rgba(0,0,0,0.4)] z-20">
                                <div className="flex items-center justify-center gap-4">
                                    <div className="w-14 text-center text-gray-500">-</div>
                                    <div className="w-14 text-center">{FMT_PCT(lTotalMode)}</div>
                                    <div className="w-24 text-center">{FMT_RANGE(lTotalCrMin, lTotalCrMax)}</div>
                                </div>
                            </td>
                        )}
                    </tr>
                )})}
            </React.Fragment>
        );
    };

    // Breadcrumb
    const breadcrumb = (
        <div className="flex items-center gap-2 text-sm">
            <button onClick={() => { setView('monthly'); setSelectedMonth(null); setSelectedWeek(null); }}
                className={`font-semibold transition-colors ${view === 'monthly' ? 'text-blue-400' : 'text-gray-400 hover:text-gray-200'
                    }`}>Monthly</button>
            {selectedMonth && (
                <>
                    <ChevronRight size={14} className="text-gray-600" />
                    <button onClick={() => { setView('weekly'); setSelectedWeek(null); }}
                        className={`font-semibold transition-colors ${view === 'weekly' ? 'text-blue-400' : 'text-gray-400 hover:text-gray-200'
                            }`}>
                        {MONTH_NAMES[selectedMonth - 1]} - Weekly
                    </button>
                </>
            )}
            {selectedWeek && (
                <>
                    <ChevronRight size={14} className="text-gray-600" />
                    <span className="text-blue-400 font-semibold">W{selectedWeek} - Daily</span>
                </>
            )}
        </div>
    );

    return (
        <div className="flex flex-col h-full gap-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between bg-[#1a1d29] p-4 rounded-xl border border-gray-800 shrink-0">
                <div>
                    <h2 className="text-xl font-bold text-gray-100 flex items-center gap-2">
                        <BarChart2 className="w-5 h-5 text-blue-400" />
                        Performance Overview
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">Backtest vs Live vs Client Range - click a period to drill down</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowFamilyModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg transition-colors cursor-pointer"
                        title="Manage Family Account Groups Setup"
                    >
                        <Users size={14} />
                        <span>Family Setup</span>
                        {familyGroups.length > 0 && (
                            <span className="px-1.5 py-0.2 bg-blue-500/30 text-blue-300 rounded text-[10px] font-bold">
                                {familyGroups.length}
                            </span>
                        )}
                    </button>

                    {view !== 'monthly' && (
                        <button onClick={goBack}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors">
                            <ChevronLeft size={14} /> Back
                        </button>
                    )}
                    <select
                        value={year}
                        onChange={e => { setYear(Number(e.target.value)); }}
                        className="px-3 py-1.5 text-xs bg-[#14161f] border border-gray-700 rounded-lg text-gray-300 focus:outline-none focus:border-blue-500"
                        style={{ colorScheme: 'dark' }}
                    >
                        {[2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    <button onClick={fetchData}
                        className="p-2 text-gray-400 hover:text-white bg-gray-800/50 hover:bg-gray-700 rounded-lg transition-all">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 px-2 shrink-0">
                {breadcrumb}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-6 px-2 shrink-0">
                <div className="flex items-center gap-1.5 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full bg-purple-400 inline-block" />
                    <span className="text-gray-400">Backtest</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block" />
                    <span className="text-gray-400">Live (Optimus: HV00061 | Pledge+: DYA441 | MCX: CA00246)</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full bg-orange-300 inline-block" />
                    <span className="text-gray-400">CR = Client Range (all clients min%-max%)</span>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs text-red-400 flex items-center gap-2 shrink-0">
                    <AlertCircle size={14} /> {error}
                </div>
            )}

            {/* Table */}
            <div className="flex-1 overflow-auto custom-scrollbar bg-[#1a1d29] border border-gray-800 rounded-xl">
                {loading ? (
                    <div className="flex items-center justify-center h-40 gap-2 text-gray-400 text-sm">
                        <Loader2 className="w-5 h-5 animate-spin" /> Loading performance data...
                    </div>
                ) : periods.length === 0 ? (
                    <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
                        No data found for the selected period.
                    </div>
                ) : (
                    <table className="w-full text-left border-collapse" style={{ minWidth: `${200 + periods.length * 240 + (view === 'monthly' ? 250 : 0)}px` }}>
                        <thead className="bg-[#14161f] sticky top-0 z-30">
                            {/* Period row */}
                            <tr>
                                <th className="px-4 py-2 text-xs font-bold text-gray-500 uppercase bg-[#14161f] sticky left-0 z-40 border-r border-gray-800" rowSpan={2}>
                                    Strategy
                                </th>
                                {periods.map((p, i) => renderPeriodHeader(p, i))}
                                {view === 'monthly' && (
                                    <th className="px-4 py-2 text-xs font-bold text-blue-300 uppercase bg-[#1e2336] sticky right-0 z-40 border-l-2 border-blue-500/40 shadow-[-6px_0_15px_rgba(0,0,0,0.4)]" rowSpan={2}>
                                        <div className="flex flex-col items-center gap-1.5">
                                            <div className="tracking-widest">Total PnL</div>
                                            <div className="flex items-center justify-center gap-4 text-[10px] font-bold mt-1">
                                                <span className="w-14 text-center text-purple-400 bg-[#14161f]/50 px-1 py-0.5 rounded">BT</span>
                                                <span className="w-14 text-center text-blue-400 bg-[#14161f]/50 px-1 py-0.5 rounded">Live</span>
                                                <span className="w-24 text-center text-orange-300 bg-[#14161f]/50 px-1 py-0.5 rounded">CR</span>
                                            </div>
                                        </div>
                                    </th>
                                )}
                            </tr>
                            {/* Sub-header row: Backtest | Live | CR */}
                            <tr className="bg-[#14161f]">
                                {renderSubHeaders()}
                            </tr>
                        </thead>
                        <tbody className="text-xs text-gray-300">
                            {strategies.length === 0 ? (
                                <tr><td colSpan={1 + periods.length * 3 + (view === 'monthly' ? 1 : 0)} className="text-center py-10 text-gray-500">No strategies found.</td></tr>
                            ) : (
                                strategies.map((strat, idx) => {
                                    const getCategory = (s) => {
                                        if (s.startsWith('Optimus')) return 'Optimus Indices';
                                        if (s.startsWith('Pledge+')) {
                                            const isIndex = ['Nifty', 'Banknifty', 'Finnifty', 'Midcapnifty', 'Sensex'].some(i => s.includes(i));
                                            return isIndex ? 'Pledge+ Indices' : 'Pledge+ Commodities';
                                        }
                                        if (s.startsWith('Commodity Options')) return 'Commodity Options';
                                        if (s === 'SGX GIFT NIFTY') return 'SGX Gift Nifty';
                                        return 'Other';
                                    };
                                    
                                    const currentCat = getCategory(strat);
                                    const prevCat = idx > 0 ? getCategory(strategies[idx - 1]) : null;
                                    const isFirstInCat = idx === 0 || currentCat !== prevCat;

                                    const getCatStyles = (cat) => {
                                        switch(cat) {
                                            case 'Optimus Indices': return { bg: 'bg-blue-950/30', text: 'text-blue-400', border: 'border-blue-500/20', dot: 'bg-blue-400' };
                                            case 'Pledge+ Indices': return { bg: 'bg-purple-950/30', text: 'text-purple-400', border: 'border-purple-500/20', dot: 'bg-purple-400' };
                                            case 'Pledge+ Commodities': return { bg: 'bg-amber-950/30', text: 'text-amber-400', border: 'border-amber-500/20', dot: 'bg-amber-400' };
                                            case 'Commodity Options': return { bg: 'bg-rose-950/30', text: 'text-rose-400', border: 'border-rose-500/20', dot: 'bg-rose-400' };
                                            case 'SGX Gift Nifty': return { bg: 'bg-cyan-950/30', text: 'text-cyan-400', border: 'border-cyan-500/20', dot: 'bg-cyan-400' };
                                            default: return { bg: 'bg-gray-900/40', text: 'text-gray-400', border: 'border-gray-800', dot: 'bg-gray-500' };
                                        }
                                    };

                                    const style = getCatStyles(currentCat);
                                    const totalRowStyle = { bg: 'bg-emerald-950/40', text: 'text-emerald-400', border: 'border-emerald-500/40', dot: 'bg-emerald-400' };
                                    const nextCat = idx < strategies.length - 1 ? getCategory(strategies[idx + 1]) : null;
                                    const isLastInCat = idx === strategies.length - 1 || nextCat !== currentCat;
                                    const catStrats = strategies.filter(s => getCategory(s) === currentCat);

                                    const isCollapsed = !!collapsedCategories[currentCat];

                                    return (
                                        <React.Fragment key={`group-${strat}`}>
                                            {isFirstInCat && (
                                                <tr
                                                    className={`${style.bg} border-y ${style.border} cursor-pointer hover:opacity-80 transition-opacity`}
                                                    onClick={() => toggleCategory(currentCat)}
                                                    title="Click to collapse or expand section"
                                                >
                                                    <td colSpan={1 + periods.length * 3 + (view === 'monthly' ? 1 : 0)} className="py-2 px-4 text-left">
                                                        <div className="flex items-center gap-2 sticky left-4 w-max z-10">
                                                            <span className="text-gray-400 hover:text-white transition-colors">
                                                                {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                                                            </span>
                                                            <span className={`w-2 h-2 rounded-full ${style.dot}`}></span>
                                                            <span className={`text-[10px] font-black uppercase tracking-widest ${style.text}`}>
                                                                {currentCat}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {!isCollapsed && renderRow(strat)}
                                            {isLastInCat && catStrats.length > 1 && (() => {
                                                let catTotalBt = 0, hasCatTotalBt = false;
                                                let catTotalLive = 0, hasCatTotalLive = false;
                                                let catTotalCrMin = null, catTotalCrMax = null;

                                                if (view === 'monthly') {
                                                    periods.forEach(p => {
                                                        catStrats.forEach(s => {
                                                            const row = tableData[s] || {};
                                                            const cell = row[p] || {};
                                                            if (cell.backtest_pct !== undefined && cell.backtest_pct !== null) {
                                                                catTotalBt += Number(cell.backtest_pct);
                                                                hasCatTotalBt = true;
                                                            }
                                                            if (cell.live_pct !== undefined && cell.live_pct !== null) {
                                                                catTotalLive += Number(cell.live_pct);
                                                                hasCatTotalLive = true;
                                                            }
                                                            if (cell.cr_min !== null && cell.cr_min !== undefined) {
                                                                catTotalCrMin = (catTotalCrMin === null) ? Number(cell.cr_min) : catTotalCrMin + Number(cell.cr_min);
                                                            }
                                                            if (cell.cr_max !== null && cell.cr_max !== undefined) {
                                                                catTotalCrMax = (catTotalCrMax === null) ? Number(cell.cr_max) : catTotalCrMax + Number(cell.cr_max);
                                                            }
                                                        });
                                                    });
                                                }

                                                return (
                                                    <tr className={`${totalRowStyle.bg} border-b-2 border-t ${totalRowStyle.border} font-bold text-xs`}>
                                                        <td className="px-4 py-2 bg-[#171a26] sticky left-0 z-20 border-r border-gray-800 shadow-[4px_0_10px_rgba(0,0,0,0.3)]">
                                                            <div className="flex items-center gap-2">
                                                                <span className={`w-2 h-2 rounded-full ${totalRowStyle.dot}`}></span>
                                                                <span className={`${totalRowStyle.text} font-black uppercase text-[10px] tracking-wider`}>
                                                                    Total {currentCat}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        {periods.map(p => {
                                                            let sumBt = 0, hasBt = false;
                                                            let sumLive = 0, hasLive = false;
                                                            let crMins = [], crMaxs = [];

                                                            catStrats.forEach(s => {
                                                                const row = tableData[s] || {};
                                                                const cell = row[p] || {};
                                                                if (cell.backtest_pct !== undefined && cell.backtest_pct !== null) { sumBt += Number(cell.backtest_pct); hasBt = true; }
                                                                if (cell.live_pct !== undefined && cell.live_pct !== null) { sumLive += Number(cell.live_pct); hasLive = true; }
                                                                if (cell.cr_min !== null && cell.cr_min !== undefined) crMins.push(Number(cell.cr_min));
                                                                if (cell.cr_max !== null && cell.cr_max !== undefined) crMaxs.push(Number(cell.cr_max));
                                                            });

                                                            const totalCrMin = crMins.length ? Math.min(...crMins) : null;
                                                            const totalCrMax = crMaxs.length ? Math.max(...crMaxs) : null;
                                                            const hasCr = totalCrMin !== null || totalCrMax !== null;

                                                            return (
                                                                <React.Fragment key={p}>
                                                                    <td className="px-2 py-2 text-center border-l border-[#1F2937]/50 text-xs text-purple-300 font-semibold">
                                                                        {hasBt ? FMT_PCT(sumBt) : <span className="text-gray-600">-</span>}
                                                                    </td>
                                                                    <td
                                                                        className={`px-2 py-2 text-center text-xs ${hasLive ? 'cursor-pointer hover:bg-blue-500/20 font-extrabold text-blue-300' : ''}`}
                                                                        title={hasLive ? `View ${currentCat} category clients` : ''}
                                                                        onClick={() => hasLive && handleCellClick(currentCat, p)}
                                                                    >
                                                                        {hasLive ? FMT_PCT(sumLive) : <span className="text-gray-600">-</span>}
                                                                    </td>
                                                                    <td
                                                                        className={`px-2 py-2 text-center text-xs ${hasCr ? 'cursor-pointer hover:bg-blue-500/20 font-medium' : ''}`}
                                                                        title={hasCr ? `View ${currentCat} category clients` : ''}
                                                                        onClick={() => hasCr && handleCellClick(currentCat, p)}
                                                                    >
                                                                        {FMT_RANGE(totalCrMin, totalCrMax)}
                                                                    </td>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                        {view === 'monthly' && (
                                                            <td className="px-4 py-2 text-center sticky right-0 bg-[#1e2336] border-l-2 border-blue-500/40 shadow-[-6px_0_15px_rgba(0,0,0,0.4)] z-20">
                                                                <div className="flex items-center justify-center gap-4 text-xs font-bold">
                                                                    <div className="w-14 text-center text-purple-300">
                                                                        {hasCatTotalBt ? FMT_PCT(catTotalBt) : <span className="text-gray-500">-</span>}
                                                                    </div>
                                                                    <div className="w-14 text-center text-emerald-300 font-extrabold">
                                                                        {hasCatTotalLive ? FMT_PCT(catTotalLive) : <span className="text-gray-500">-</span>}
                                                                    </div>
                                                                    <div className="w-24 text-center text-orange-300">
                                                                        {FMT_RANGE(catTotalCrMin, catTotalCrMax)}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })()}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Performance Clients List and Client Audit Detail Modals */}
            {selectedCell && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4">
                    <div className="bg-[#1a1d29] border border-gray-800 rounded-xl shadow-2xl w-full max-w-6xl xl:max-w-7xl h-[90vh] max-h-[90vh] flex flex-col overflow-hidden">

                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-[#14161f]">
                            <div className="flex items-center gap-2">
                                {selectedClient && (
                                    <button onClick={() => setSelectedClient(null)} className="p-1 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors">
                                        <ChevronLeft size={18} />
                                    </button>
                                )}
                                <div>
                                    <h3 className="font-bold text-gray-100 text-base flex items-center flex-wrap gap-2">
                                        {selectedClient ? (
                                            <>
                                                <span>Audit Match: {selectedClient.client_id}</span>
                                                {selectedClient.name && selectedClient.name !== '-' && (
                                                    <span className="text-xs text-gray-400 font-normal">({selectedClient.name})</span>
                                                )}
                                                {selectedClient.label && selectedClient.label !== '-' && (
                                                    <span className={`px-2 py-0.5 rounded text-[9px] font-semibold tracking-wide uppercase ${getLabelStyles(selectedClient.label)}`}>
                                                        {selectedClient.label}
                                                    </span>
                                                )}
                                            </>
                                        ) : selectedCell.view === 'monthly' ? (
                                            `Clients traded in ${selectedCell.period} ${selectedCell.year}`
                                        ) : selectedCell.view === 'weekly' ? (
                                            `Clients traded in ${MONTH_NAMES[selectedCell.month - 1]} ${selectedCell.period}`
                                        ) : (
                                            `Clients traded on ${selectedCell.date}`
                                        )}
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {selectedCell.strat}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {!selectedClient && clientsList.length > 0 && (
                                    <>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileUpload}
                                            accept=".csv"
                                            className="hidden"
                                        />
                                        {uploadedAuditCsv ? (
                                            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 rounded-lg text-xs font-medium">
                                                <span className="max-w-[150px] truncate" title={uploadedAuditCsv.fileName}>
                                                    📁 {uploadedAuditCsv.fileName}
                                                </span>
                                                <button
                                                    onClick={() => {
                                                        setUploadedAuditCsv(null);
                                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                                    }}
                                                    className="text-gray-400 hover:text-white p-0.5 transition-colors cursor-pointer"
                                                    title="Remove CSV"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-wider rounded-lg border border-emerald-500/30 transition-colors mr-1 cursor-pointer"
                                                title="Upload CSV for Manual PnL Audit Comparison"
                                            >
                                                <Upload size={14} />
                                                <span className="hidden sm:inline">Upload Audit CSV</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={handleDownloadCSV}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs font-semibold uppercase tracking-wider rounded-lg border border-blue-500/30 transition-colors mr-1 cursor-pointer"
                                            title="Download CSV"
                                        >
                                            <Download size={14} />
                                            <span className="hidden sm:inline">Download CSV</span>
                                        </button>
                                    </>
                                )}
                                <button onClick={() => setSelectedCell(null)} className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors">
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-hidden p-4 flex flex-col min-h-0">
                            {!selectedClient ? (
                                // Clients List View
                                clientsLoading ? (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
                                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                                        <span className="text-sm">
                                            {selectedCell.view === 'monthly' ? 'Fetching monthly client list...' :
                                             selectedCell.view === 'weekly' ? 'Fetching weekly client list...' :
                                             'Fetching daily client list...'}
                                        </span>
                                    </div>
                                ) : clientsList.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                                        <AlertCircle className="w-8 h-8 mb-2 opacity-50 text-orange-400" />
                                        <span className="text-sm">
                                            {selectedCell.view === 'monthly' ? 'No client trades found for this month.' :
                                             selectedCell.view === 'weekly' ? 'No client trades found for this week.' :
                                             'No client trades found for this day.'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                                        {/* Search & Mode Switcher Controls */}
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#14161f]/40 p-3 rounded-xl border border-gray-800/80 shrink-0">
                                            {/* Left: Search & Broker Filter */}
                                            <div className="flex items-center gap-2 flex-1 max-w-lg">
                                                <div className="relative flex-1">
                                                    <Search size={14} className="absolute left-3 top-2.5 text-gray-500" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search Client ID, Name, Family, Label..."
                                                        className="bg-[#14161f] border border-gray-700 rounded-lg w-full pl-9 pr-8 py-2 text-xs text-gray-200 focus:outline-none focus:border-blue-500 transition-colors"
                                                        value={clientSearchQuery}
                                                        onChange={(e) => setClientSearchQuery(e.target.value)}
                                                    />
                                                    {clientSearchQuery && (
                                                        <button
                                                            onClick={() => setClientSearchQuery('')}
                                                            className="absolute right-2.5 top-2.5 text-gray-500 hover:text-white"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                                <select
                                                    value={selectedBroker}
                                                    onChange={(e) => setSelectedBroker(e.target.value)}
                                                    className="bg-[#14161f] border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-200 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                                                    style={{ colorScheme: 'dark' }}
                                                >
                                                    <option value="All">All Brokers</option>
                                                    <option value="Fyers">Fyers</option>
                                                    <option value="DhanHQ">Dhan HQ</option>
                                                    <option value="DhanXTS">Dhan XTS</option>
                                                    <option value="Nubra">Nubra</option>
                                                </select>
                                            </div>

                                            {/* Center: Mode Toggle (Individual vs Family) */}
                                            <div className="flex items-center bg-[#0e1017] p-1 rounded-xl border border-gray-800 shrink-0 shadow-inner">
                                                <button
                                                    type="button"
                                                    onClick={() => setModalViewMode('individual')}
                                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                                                        modalViewMode === 'individual'
                                                            ? 'bg-blue-600 text-white shadow-md'
                                                            : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/40'
                                                    }`}
                                                >
                                                    <User size={13} />
                                                    <span>Individual Accounts</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setModalViewMode('family')}
                                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                                                        modalViewMode === 'family'
                                                            ? 'bg-blue-600 text-white shadow-md'
                                                            : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/40'
                                                    }`}
                                                >
                                                    <Users size={13} />
                                                    <span>Family PnL View</span>
                                                    {familyGroups.length > 0 && (
                                                        <span className="px-1.5 py-0.2 rounded-full text-[9px] bg-blue-950 text-blue-300 font-bold border border-blue-500/30">
                                                            {familyGroups.length}
                                                        </span>
                                                    )}
                                                </button>
                                            </div>

                                            {/* Right: Quick actions & Stats */}
                                            <div className="flex items-center gap-2 shrink-0">
                                                {modalViewMode === 'family' ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleAllFamilies(true)}
                                                            className="px-2.5 py-1.5 text-[11px] font-semibold bg-gray-800/80 hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700/80 transition-colors cursor-pointer"
                                                        >
                                                            Expand All
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleAllFamilies(false)}
                                                            className="px-2.5 py-1.5 text-[11px] font-semibold bg-gray-800/80 hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700/80 transition-colors cursor-pointer"
                                                        >
                                                            Collapse All
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowFamilyModal(true)}
                                                            className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg border border-blue-500/20 transition-colors cursor-pointer"
                                                            title="Configure Families"
                                                        >
                                                            <Users size={14} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    /* Highlights Legend */
                                                    <div className="flex items-center gap-4 text-[10px]">
                                                        {minClient && (
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="w-2.5 h-2.5 rounded bg-red-500/20 border border-red-500/40 inline-block" />
                                                                <span className="text-gray-400">Min PnL ({minClient.client_id})</span>
                                                            </div>
                                                        )}
                                                        {maxClient && (
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="w-2.5 h-2.5 rounded bg-emerald-500/20 border border-emerald-500/40 inline-block" />
                                                                <span className="text-gray-400">Max PnL ({maxClient.client_id})</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Table Container with Sticky Headers */}
                                        <div className="flex-1 overflow-auto border border-gray-800 rounded-xl bg-[#14161f]/50 shadow-xl custom-scrollbar relative min-h-0">
                                            <table className="w-full text-left border-separate border-spacing-0 text-xs">
                                                <thead className="sticky top-0 z-20 shadow-md bg-[#0f1118]">
                                                    {/* Tier 1: Group Super-Headers */}
                                                    <tr className="bg-[#0f1118] text-gray-400 uppercase font-bold text-[9px] select-none tracking-wider">
                                                        <th rowSpan={2} className="px-3 py-2 text-center text-gray-600 w-10 border-r border-b border-gray-800 bg-[#0f1118]">#</th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors whitespace-nowrap border-b border-gray-800 bg-[#0f1118]" onClick={() => handleClientSort(clientSortConfig.key === 'name' ? 'client_id' : 'name')}>
                                                            <div className="flex items-center gap-1">
                                                                {modalViewMode === 'family' ? 'Family / Client Account' : 'Client Account'}
                                                                {(clientSortConfig.key === 'client_id' || clientSortConfig.key === 'name') && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors whitespace-nowrap border-b border-gray-800 bg-[#0f1118]" onClick={() => handleClientSort('label')}>
                                                            <div className="flex items-center gap-1">
                                                                Label
                                                                {clientSortConfig.key === 'label' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors text-right whitespace-nowrap border-b border-gray-800 bg-[#0f1118]" onClick={() => handleClientSort('trades_count')}>
                                                            <div className="flex items-center justify-end gap-1">
                                                                Trades
                                                                {clientSortConfig.key === 'trades_count' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors text-right whitespace-nowrap border-b border-gray-800 bg-[#0f1118]" onClick={() => handleClientSort('multiplier')}>
                                                            <div className="flex items-center justify-end gap-1">
                                                                Mult.
                                                                {clientSortConfig.key === 'multiplier' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors text-right whitespace-nowrap border-b border-gray-800 bg-[#0f1118]" onClick={() => handleClientSort('deployed_capital')}>
                                                            <div className="flex items-center justify-end gap-1">
                                                                Deployed
                                                                {clientSortConfig.key === 'deployed_capital' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors text-right whitespace-nowrap border-b border-gray-800 bg-[#0f1118]" onClick={() => handleClientSort('available_funds')}>
                                                            <div className="flex items-center justify-end gap-1">
                                                                Avail. Funds
                                                                {clientSortConfig.key === 'available_funds' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors text-right whitespace-nowrap border-b border-gray-800 bg-[#0f1118]" onClick={() => handleClientSort('delta_funds')}>
                                                            <div className="flex items-center justify-end gap-1">
                                                                Delta Funds
                                                                {clientSortConfig.key === 'delta_funds' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors text-right whitespace-nowrap border-b border-gray-800 bg-[#0f1118]" onClick={() => handleClientSort('ratio')} title="Ratio = Deployed Capital / Available Funds">
                                                            <div className="flex items-center justify-end gap-1">
                                                                Ratio
                                                                {clientSortConfig.key === 'ratio' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th rowSpan={2} className="px-3 py-2 cursor-pointer hover:text-white transition-colors text-center border-r border-b border-gray-800 whitespace-nowrap bg-[#0f1118]" onClick={() => handleClientSort('balance_updated_at')} title="Timestamp when available funds was last updated">
                                                            <div className="flex items-center justify-center gap-1">
                                                                Funds Updated
                                                                {clientSortConfig.key === 'balance_updated_at' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>

                                                        {/* Super Header 1: System Performance */}
                                                        <th colSpan={4} className="px-3 py-1.5 text-center bg-[#101b33] text-blue-300 border-r border-b border-gray-800 font-extrabold tracking-wider">
                                                            GodsEye System (Net PnL Flow)
                                                        </th>

                                                        {/* Super Header 2: Broker Statement (CSV) */}
                                                        {uploadedAuditCsv && (
                                                            <th colSpan={2} className="px-3 py-1.5 text-center bg-[#0a271c] text-emerald-300 border-r border-b border-gray-800 font-extrabold tracking-wider">
                                                                Broker CSV ({uploadedAuditCsv.fileName?.length > 18 ? uploadedAuditCsv.fileName.slice(0, 18) + '…' : uploadedAuditCsv.fileName})
                                                            </th>
                                                        )}

                                                        {/* Super Header 3: Audit Variance */}
                                                        {uploadedAuditCsv && (
                                                            <th colSpan={2} className="px-3 py-1.5 text-center bg-[#281508] text-amber-300 border-b border-gray-800 font-extrabold tracking-wider">
                                                                Audit Variance (Net - Broker)
                                                            </th>
                                                        )}
                                                    </tr>

                                                    {/* Tier 2: Sub-headers */}
                                                    <tr className="bg-[#14161f] text-gray-400 uppercase font-bold text-[10px] select-none whitespace-nowrap">
                                                        <th className="px-3 py-2.5 cursor-pointer hover:text-white transition-colors text-right bg-[#101b33] text-gray-300 border-b border-gray-800" onClick={() => handleClientSort('total_pnl')} title="Gross trading PnL before charges">
                                                            <div className="flex items-center justify-end gap-1">
                                                                Gross PnL
                                                                {clientSortConfig.key === 'total_pnl' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-400" /> : <ArrowDown size={11} className="text-blue-400" />)}
                                                            </div>
                                                        </th>
                                                        <th className="px-3 py-2.5 hover:text-white transition-colors text-right bg-[#2a1708] text-amber-400 border-l border-b border-gray-800/60 relative">
                                                            <div className="flex items-center justify-end gap-1.5">
                                                                <div
                                                                    className="flex items-center gap-1 cursor-pointer"
                                                                    onClick={() => handleClientSort('total_charges')}
                                                                    title="Sort by Charges"
                                                                >
                                                                    <span>Charges</span>
                                                                    {clientSortConfig.key === 'total_charges' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-amber-400" /> : <ArrowDown size={11} className="text-amber-400" />)}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setChargesPopup(prev => prev?.type === 'rates' ? null : { type: 'rates' });
                                                                    }}
                                                                    className="p-1 rounded text-amber-400/80 hover:text-amber-200 hover:bg-amber-400/20 transition-all cursor-pointer"
                                                                    title="Click to view & copy official FYERS charge list"
                                                                >
                                                                    <Info size={13} />
                                                                </button>
                                                            </div>
                                                        </th>
                                                        <th className="px-3 py-2.5 cursor-pointer hover:text-white transition-colors text-right bg-[#152342] text-blue-300 border-l border-b border-gray-800/60 font-black" onClick={() => handleClientSort('net_pnl')} title="Net PnL = Gross PnL - Charges">
                                                            <div className="flex items-center justify-end gap-1">
                                                                Net PnL
                                                                {clientSortConfig.key === 'net_pnl' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-300" /> : <ArrowDown size={11} className="text-blue-300" />)}
                                                            </div>
                                                        </th>
                                                        <th className="px-3 py-2.5 cursor-pointer hover:text-white transition-colors text-right bg-[#152342] text-blue-300 border-r border-b border-gray-800 font-black" onClick={() => handleClientSort('net_pnl_pct')} title="Net yield % on deployed capital">
                                                            <div className="flex items-center justify-end gap-1">
                                                                Net %
                                                                {clientSortConfig.key === 'net_pnl_pct' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-blue-300" /> : <ArrowDown size={11} className="text-blue-300" />)}
                                                            </div>
                                                        </th>

                                                        {uploadedAuditCsv && (
                                                            <>
                                                                <th className="px-3 py-2.5 cursor-pointer hover:text-white transition-colors text-right bg-[#0a271c] text-emerald-400 border-b border-gray-800" onClick={() => handleClientSort('manual_pnl')} title="PnL from uploaded broker statement">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        Broker PnL
                                                                        {clientSortConfig.key === 'manual_pnl' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-emerald-400" /> : <ArrowDown size={11} className="text-emerald-400" />)}
                                                                    </div>
                                                                </th>
                                                                <th className="px-3 py-2.5 cursor-pointer hover:text-white transition-colors text-right bg-[#0a271c] text-emerald-400 border-r border-b border-gray-800" onClick={() => handleClientSort('manual_pnl_pct')} title="Broker yield % on deployed capital">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        Broker %
                                                                        {clientSortConfig.key === 'manual_pnl_pct' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-emerald-400" /> : <ArrowDown size={11} className="text-emerald-400" />)}
                                                                    </div>
                                                                </th>
                                                            </>
                                                        )}

                                                        {uploadedAuditCsv && (
                                                            <>
                                                                <th className="px-3 py-2.5 cursor-pointer hover:text-white transition-colors text-right bg-[#281508] text-amber-400 border-b border-gray-800" onClick={() => handleClientSort('delta_pnl')} title="Net PnL - Broker PnL">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        Diff (₹)
                                                                        {clientSortConfig.key === 'delta_pnl' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-amber-400" /> : <ArrowDown size={11} className="text-amber-400" />)}
                                                                    </div>
                                                                </th>
                                                                <th className="px-3 py-2.5 cursor-pointer hover:text-white transition-colors text-right bg-[#281508] text-amber-400 border-b border-gray-800" onClick={() => handleClientSort('delta_pnl_pct')} title="Net % - Broker %">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        Diff (%)
                                                                        {clientSortConfig.key === 'delta_pnl_pct' && (clientSortConfig.direction === 'asc' ? <ArrowUp size={11} className="text-amber-400" /> : <ArrowDown size={11} className="text-amber-400" />)}
                                                                    </div>
                                                                </th>
                                                            </>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                                    {modalViewMode === 'family' ? (
                                                        /* ─── FAMILY GROUPED VIEW ─── */
                                                        familyGroupedData.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={14 + (uploadedAuditCsv ? 4 : 0)} className="text-center py-8 text-gray-500">
                                                                    No families or matching client accounts found.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            familyGroupedData.map((g, gIdx) => {
                                                                const expanded = isFamilyExpanded(g.family_name);
                                                                const t = g.totals;
                                                                const hasFlags = t.flagged_count > 0;

                                                                return (
                                                                    <React.Fragment key={g.family_name}>
                                                                        {/* Family Super-Row */}
                                                                        <tr
                                                                            onClick={() => toggleFamilyAccordion(g.family_name)}
                                                                            className={`cursor-pointer transition-colors border-y border-gray-800/80 font-semibold select-none ${
                                                                                g.isUnassigned
                                                                                    ? 'bg-[#181b24] hover:bg-[#1f2330] text-gray-300'
                                                                                    : 'bg-[#141824] hover:bg-[#1a2030] text-gray-100'
                                                                            }`}
                                                                            title="Click to expand / collapse family accounts"
                                                                        >
                                                                            <td className="px-3 py-3 text-center border-r border-gray-800/60">
                                                                                <span className="inline-flex items-center justify-center p-1 rounded hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors">
                                                                                    {expanded ? <ChevronDown size={14} className="text-blue-400" /> : <ChevronRight size={14} />}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-3 py-3 whitespace-nowrap">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span
                                                                                        className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm"
                                                                                        style={{ backgroundColor: g.color || '#3B82F6' }}
                                                                                    />
                                                                                    <span className="font-bold text-xs text-gray-100 flex items-center gap-1.5">
                                                                                        {g.family_name}
                                                                                        <span className="text-[10px] text-gray-400 font-normal">
                                                                                            ({g.clients.length} {g.clients.length === 1 ? 'account' : 'accounts'})
                                                                                        </span>
                                                                                    </span>
                                                                                    {hasFlags && (
                                                                                        <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1 shrink-0">
                                                                                            <Flag size={9} className="fill-amber-400" />
                                                                                            {t.flagged_count} Flagged
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-3 py-3 text-gray-400 text-[11px] whitespace-nowrap">
                                                                                {g.isUnassigned ? (
                                                                                    <span className="text-gray-500 italic text-[10px]">Unassigned</span>
                                                                                ) : (
                                                                                    <span className="text-blue-400/80 font-medium text-[10px]">Family Group</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-3 py-3 text-right font-mono whitespace-nowrap font-bold text-gray-200">
                                                                                {t.trades_count}
                                                                            </td>
                                                                            <td className="px-3 py-3 text-right font-mono font-bold text-cyan-300 whitespace-nowrap">
                                                                                {t.multiplier > 0 ? (Number.isInteger(t.multiplier) ? t.multiplier : t.multiplier.toFixed(2)) : '-'}
                                                                            </td>
                                                                            <td className="px-3 py-3 text-right font-mono font-bold text-blue-300 whitespace-nowrap">
                                                                                ₹{t.deployed_capital.toLocaleString('en-IN')}
                                                                            </td>
                                                                            <td className="px-3 py-3 text-right font-mono font-bold text-cyan-300 whitespace-nowrap">
                                                                                {(t.available_funds < 0 ? '-' : '') + '₹' + Math.abs(t.available_funds).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </td>
                                                                            <td className="px-3 py-3 text-right font-mono font-bold text-purple-300 whitespace-nowrap">
                                                                                {(t.delta_funds < 0 ? '-' : '') + '₹' + Math.abs(t.delta_funds).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </td>
                                                                            <td className="px-3 py-3 text-right font-mono font-bold text-amber-300 whitespace-nowrap" title={`Ratio: ₹${t.deployed_capital.toLocaleString('en-IN')} / ₹${t.available_funds.toLocaleString('en-IN')}`}>
                                                                                {t.ratio !== undefined && t.ratio !== null && !isNaN(t.ratio) && isFinite(t.ratio) ? t.ratio.toFixed(2) : '-'}
                                                                            </td>
                                                                            <td className="px-3 py-3 text-center font-mono text-[11px] text-gray-400 border-r border-gray-800 whitespace-nowrap" title={t.latest_balance_updated_at ? `Latest Funds Updated: ${t.latest_balance_updated_at}` : '-'}>
                                                                                {t.latest_balance_updated_at ? FMT_DATETIME_SHORT(t.latest_balance_updated_at) : '-'}
                                                                            </td>

                                                                            {/* Section 1: GodsEye System Performance */}
                                                                            <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-blue-950/10 ${t.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                                                {t.total_pnl >= 0 ? '+' : ''}{t.total_pnl.toFixed(2)}
                                                                            </td>
                                                                            <td className="px-3 py-3 text-right font-mono font-bold text-amber-400 whitespace-nowrap bg-amber-950/20 border-l border-gray-800/40">
                                                                                ₹{t.total_charges.toFixed(2)}
                                                                            </td>
                                                                            <td className={`px-3 py-3 text-right font-mono font-extrabold whitespace-nowrap bg-blue-950/20 border-l border-gray-800/40 ${t.net_pnl >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                                                                                {t.net_pnl >= 0 ? '+' : ''}{t.net_pnl.toFixed(2)}
                                                                            </td>
                                                                            <td className={`px-3 py-3 text-right font-mono font-extrabold whitespace-nowrap bg-blue-950/20 border-r border-gray-800/80 ${t.net_pnl_pct >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                                                                                {t.net_pnl_pct >= 0 ? '+' : ''}{t.net_pnl_pct.toFixed(2)}%
                                                                            </td>

                                                                            {/* Section 2: Broker Statement (CSV) */}
                                                                            {uploadedAuditCsv && (
                                                                                <>
                                                                                    <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-emerald-950/15 ${t.hasManualPnl ? (t.manual_pnl >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-600'}`}>
                                                                                        {t.hasManualPnl ? `${t.manual_pnl >= 0 ? '+' : ''}${t.manual_pnl.toFixed(2)}` : '-'}
                                                                                    </td>
                                                                                    <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-emerald-950/15 border-r border-gray-800/80 ${t.hasManualPnl ? (t.manual_pnl_pct >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-600'}`}>
                                                                                        {t.hasManualPnl ? `${t.manual_pnl_pct >= 0 ? '+' : ''}${t.manual_pnl_pct.toFixed(2)}%` : '-'}
                                                                                    </td>
                                                                                </>
                                                                            )}

                                                                            {/* Section 3: Audit Variance */}
                                                                            {uploadedAuditCsv && (
                                                                                <>
                                                                                    <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-amber-950/20 ${t.hasManualPnl ? (Math.abs(t.delta_pnl) < 1 ? 'text-emerald-400' : 'text-amber-400') : 'text-gray-600'}`}>
                                                                                        {t.hasManualPnl ? `${t.delta_pnl > 0 ? '+' : ''}${t.delta_pnl.toFixed(2)}` : '-'}
                                                                                    </td>
                                                                                    <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-amber-950/20 ${t.hasManualPnl ? (Math.abs(t.delta_pnl_pct) < 0.01 ? 'text-emerald-400' : 'text-amber-400') : 'text-gray-600'}`}>
                                                                                        {t.hasManualPnl ? `${t.delta_pnl_pct > 0 ? '+' : ''}${t.delta_pnl_pct.toFixed(2)}%` : '-'}
                                                                                    </td>
                                                                                </>
                                                                            )}
                                                                        </tr>

                                                                        {/* Nested Client Rows */}
                                                                        {expanded && g.clients.map((c, cIdx) => {
                                                                            const isMin = minClient && c.client_id === minClient.client_id;
                                                                            const isMax = maxClient && c.client_id === maxClient.client_id;

                                                                            let rowClass = "bg-[#11131c] hover:bg-blue-600/10 cursor-pointer transition-colors border-l-2 border-transparent hover:border-blue-500";
                                                                            if (isMax) {
                                                                                rowClass = "bg-[#11181f] text-emerald-100 hover:bg-emerald-500/10 cursor-pointer transition-colors border-l-2 border-emerald-500";
                                                                            } else if (isMin) {
                                                                                rowClass = "bg-[#181116] text-red-100 hover:bg-red-500/10 cursor-pointer transition-colors border-l-2 border-red-500";
                                                                            }

                                                                            return (
                                                                                <tr key={c.client_id} onClick={() => handleClientClick(c)} className={rowClass}>
                                                                                    <td className="px-3 py-2.5 text-center text-gray-600 text-[10px] font-mono border-r border-gray-800/60 pl-6">
                                                                                        <span className="text-gray-600 font-mono text-[11px] mr-1">↳</span>
                                                                                        {cIdx + 1}
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 whitespace-nowrap pl-6">
                                                                                        <div className="flex items-center gap-2">
                                                                                            {/* Client Name First */}
                                                                                            <span className="text-gray-100 font-bold text-xs" title={c.name}>
                                                                                                {c.name || '-'}
                                                                                            </span>

                                                                                            {/* Client ID in font-mono */}
                                                                                            <span className={`font-mono text-[11px] ${isMax ? 'text-emerald-400 font-bold' : isMin ? 'text-red-400 font-bold' : 'text-blue-400 font-semibold'}`}>
                                                                                                ({c.client_id})
                                                                                            </span>

                                                                                            {/* Boolean Flag Interactive Button */}
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleQuickToggleFlag(c.client_id, !!c.flag);
                                                                                                }}
                                                                                                className={`p-0.5 rounded transition-all cursor-pointer ${
                                                                                                    c.flag
                                                                                                        ? 'text-amber-400 hover:text-amber-300'
                                                                                                        : 'text-gray-600 hover:text-amber-400'
                                                                                                }`}
                                                                                                title={c.flag ? "Flagged (Boolean: true) - click to unflag" : "Flag account (Boolean: false) - click to flag"}
                                                                                            >
                                                                                                <Flag size={11} className={c.flag ? 'fill-amber-400 text-amber-400' : ''} />
                                                                                            </button>

                                                                                            {isMax && <span className="px-1.5 py-0.2 rounded text-[8px] bg-emerald-500/20 text-emerald-300 font-bold uppercase tracking-wide">Max</span>}
                                                                                            {isMin && <span className="px-1.5 py-0.2 rounded text-[8px] bg-red-500/20 text-red-300 font-bold uppercase tracking-wide">Min</span>}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                                                                        <div className="flex items-center gap-1">
                                                                                            {c.label && c.label !== '-' ? (
                                                                                                <span className={`px-2 py-0.5 rounded text-[9px] font-semibold tracking-wide uppercase ${getLabelStyles(c.label)}`}>
                                                                                                    {c.label}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span className="text-gray-600 text-xs">-</span>
                                                                                            )}
                                                                                            {c.family_notes && (
                                                                                                <span className="text-[9px] text-gray-500 italic max-w-[80px] truncate" title={c.family_notes}>
                                                                                                    ({c.family_notes})
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap text-xs">{c.trades_count}</td>
                                                                                    <td className="px-3 py-2.5 text-right font-mono text-gray-300 whitespace-nowrap text-xs">
                                                                                        {c.multiplier !== undefined && c.multiplier !== null && !isNaN(Number(c.multiplier)) ? (Number.isInteger(Number(c.multiplier)) ? Number(c.multiplier) : Number(c.multiplier).toFixed(2)) : '-'}
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 text-right font-mono text-blue-300 whitespace-nowrap text-xs">
                                                                                        {c.deployed_capital !== undefined ? `₹${c.deployed_capital.toLocaleString('en-IN')}` : '-'}
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 text-right font-mono text-cyan-300 whitespace-nowrap text-xs">
                                                                                        {c.available_funds !== undefined ? (c.available_funds < 0 ? '-' : '') + '₹' + Math.abs(c.available_funds).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 text-right font-mono text-purple-300 whitespace-nowrap text-xs">
                                                                                        {c.delta_funds !== undefined ? (c.delta_funds < 0 ? '-' : '') + '₹' + Math.abs(c.delta_funds).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 text-right font-mono text-amber-300 whitespace-nowrap text-xs" title={`Ratio: Deployed / Available = ${c.ratio !== undefined && !isNaN(c.ratio) && isFinite(c.ratio) ? c.ratio.toFixed(2) : '-'}`}>
                                                                                        {c.ratio !== undefined && c.ratio !== null && !isNaN(c.ratio) && isFinite(c.ratio) ? c.ratio.toFixed(2) : '-'}
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 text-center font-mono text-[10px] text-gray-400 border-r border-gray-800 whitespace-nowrap" title={c.balance_updated_at ? `Funds Updated: ${c.balance_updated_at}` : '-'}>
                                                                                        {c.balance_updated_at ? FMT_DATETIME_SHORT(c.balance_updated_at) : '-'}
                                                                                    </td>

                                                                                    {/* Section 1: GodsEye System Performance */}
                                                                                    <td className={`px-3 py-2.5 text-right font-mono font-medium whitespace-nowrap bg-blue-950/5 text-xs ${c.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                                                        {c.total_pnl >= 0 ? '+' : ''}{c.total_pnl.toFixed(2)}
                                                                                    </td>
                                                                                    <td className="px-3 py-2.5 text-right font-mono text-amber-400 whitespace-nowrap bg-amber-950/15 border-l border-gray-800/40 text-xs">
                                                                                        <div className="flex items-center justify-end gap-1">
                                                                                            <span>{c.total_charges !== undefined ? `₹${c.total_charges.toFixed(2)}` : '-'}</span>
                                                                                            {c.charges_breakdown && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        setChargesPopup(prev => prev?.id === c.client_id ? null : { type: 'client', id: c.client_id, client: c });
                                                                                                    }}
                                                                                                    className="opacity-60 hover:opacity-100 text-amber-400 hover:text-amber-200 p-0.5 rounded hover:bg-amber-400/20 transition-all cursor-pointer"
                                                                                                    title={`Click to view charges breakdown for ${c.client_id}`}
                                                                                                >
                                                                                                    <Info size={11} />
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className={`px-3 py-2.5 text-right font-mono font-bold whitespace-nowrap bg-blue-950/15 border-l border-gray-800/40 text-xs ${c.net_pnl !== undefined ? (c.net_pnl >= 0 ? 'text-emerald-300' : 'text-red-400') : 'text-gray-600'}`}>
                                                                                        {c.net_pnl !== undefined ? `${c.net_pnl >= 0 ? '+' : ''}${c.net_pnl.toFixed(2)}` : '-'}
                                                                                    </td>
                                                                                    <td className={`px-3 py-2.5 text-right font-mono font-bold whitespace-nowrap bg-blue-950/15 border-r border-gray-800/80 text-xs ${c.net_pnl_pct !== undefined ? (c.net_pnl_pct >= 0 ? 'text-emerald-300' : 'text-red-400') : 'text-gray-600'}`}>
                                                                                        {c.net_pnl_pct !== undefined ? `${c.net_pnl_pct >= 0 ? '+' : ''}${c.net_pnl_pct.toFixed(2)}%` : '-'}
                                                                                    </td>

                                                                                    {/* Section 2: Broker Statement (CSV) */}
                                                                                    {uploadedAuditCsv && (
                                                                                        <>
                                                                                            <td className={`px-3 py-2.5 text-right font-mono font-medium whitespace-nowrap bg-emerald-950/10 text-xs ${c.manual_pnl !== undefined ? (c.manual_pnl >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-600'}`}>
                                                                                                {c.manual_pnl !== undefined ? (c.manual_pnl >= 0 ? '+' : '') + c.manual_pnl.toFixed(2) : '-'}
                                                                                            </td>
                                                                                            <td className={`px-3 py-2.5 text-right font-mono font-medium whitespace-nowrap bg-emerald-950/10 border-r border-gray-800/80 text-xs ${c.manual_pnl_pct !== undefined ? (c.manual_pnl_pct >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-600'}`}>
                                                                                                {c.manual_pnl_pct !== undefined ? (c.manual_pnl_pct >= 0 ? '+' : '') + c.manual_pnl.toFixed(2) + '%' : '-'}
                                                                                            </td>
                                                                                        </>
                                                                                    )}

                                                                                    {/* Section 3: Audit Variance */}
                                                                                    {uploadedAuditCsv && (
                                                                                        <>
                                                                                            <td className={`px-3 py-2.5 text-right font-mono font-bold whitespace-nowrap bg-amber-950/15 text-xs ${c.delta_pnl !== undefined ? (Math.abs(c.delta_pnl) < 1 ? 'text-emerald-400' : 'text-amber-400') : 'text-gray-600'}`} title={`Net PnL (₹${c.net_pnl !== undefined ? c.net_pnl.toFixed(2) : '-'}) - Broker PnL (₹${c.manual_pnl !== undefined ? c.manual_pnl.toFixed(2) : '-'})`}>
                                                                                                {c.delta_pnl !== undefined ? (c.delta_pnl > 0 ? '+' : '') + c.delta_pnl.toFixed(2) : '-'}
                                                                                            </td>
                                                                                            <td className={`px-3 py-2.5 text-right font-mono font-bold whitespace-nowrap bg-amber-950/15 text-xs ${c.delta_pnl_pct !== undefined ? (Math.abs(c.delta_pnl_pct) < 0.01 ? 'text-emerald-400' : 'text-amber-400') : 'text-gray-600'}`} title={`Net % (${c.net_pnl_pct !== undefined ? c.net_pnl_pct.toFixed(2) : '-'}%) - Broker % (${c.manual_pnl_pct !== undefined ? c.manual_pnl_pct.toFixed(2) : '-'}%)`}>
                                                                                                {c.delta_pnl_pct !== undefined ? (c.delta_pnl_pct > 0 ? '+' : '') + c.delta_pnl_pct.toFixed(2) + '%' : '-'}
                                                                                            </td>
                                                                                        </>
                                                                                    )}
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </React.Fragment>
                                                                );
                                                            })
                                                        )
                                                    ) : (
                                                        /* ─── INDIVIDUAL ACCOUNTS VIEW ─── */
                                                        sortedClients.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={14 + (uploadedAuditCsv ? 4 : 0)} className="text-center py-8 text-gray-500">
                                                                    No clients match your search criteria.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            sortedClients.map((c, idx) => {
                                                                const isMin = minClient && c.client_id === minClient.client_id;
                                                                const isMax = maxClient && c.client_id === maxClient.client_id;
                                                                
                                                                let rowClass = "hover:bg-blue-600/10 cursor-pointer transition-colors border-l-2 border-transparent hover:border-blue-500";
                                                                if (isMax) {
                                                                    rowClass = "bg-emerald-500/5 text-emerald-100 hover:bg-emerald-500/10 cursor-pointer transition-colors border-l-2 border-emerald-500";
                                                                } else if (isMin) {
                                                                    rowClass = "bg-red-500/5 text-red-100 hover:bg-red-500/10 cursor-pointer transition-colors border-l-2 border-red-500";
                                                                }
                                                                
                                                                return (
                                                                    <tr key={c.client_id} onClick={() => handleClientClick(c)} className={rowClass}>
                                                                        <td className="px-3 py-3 text-center text-gray-600 text-[10px] font-mono border-r border-gray-800/60">{idx + 1}</td>
                                                                        <td className="px-3 py-3 whitespace-nowrap">
                                                                            <div className="flex items-center gap-2">
                                                                                {/* Client Name First */}
                                                                                <span className="text-gray-100 font-bold text-xs" title={c.name}>
                                                                                    {c.name || '-'}
                                                                                </span>

                                                                                {/* Client ID in font-mono */}
                                                                                <span className={`font-mono text-[11px] ${isMax ? 'text-emerald-400 font-bold' : isMin ? 'text-red-400 font-bold' : 'text-blue-400 font-semibold'}`}>
                                                                                    ({c.client_id})
                                                                                </span>

                                                                                {/* Boolean Flag Interactive Button */}
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleQuickToggleFlag(c.client_id, !!c.flag);
                                                                                    }}
                                                                                    className={`p-0.5 rounded transition-all cursor-pointer ${
                                                                                        c.flag
                                                                                            ? 'text-amber-400 hover:text-amber-300'
                                                                                            : 'text-gray-600 hover:text-amber-400'
                                                                                    }`}
                                                                                    title={c.flag ? "Flagged (Boolean: true) - click to unflag" : "Flag account (Boolean: false) - click to flag"}
                                                                                >
                                                                                    <Flag size={11} className={c.flag ? 'fill-amber-400 text-amber-400' : ''} />
                                                                                </button>

                                                                                {c.family_name && c.family_name !== 'Unassigned Accounts' && (
                                                                                    <span
                                                                                        className="px-1.5 py-0.2 rounded text-[8px] font-medium border"
                                                                                        style={{
                                                                                            borderColor: `${c.family_color || '#3B82F6'}50`,
                                                                                            color: c.family_color || '#3B82F6',
                                                                                            backgroundColor: `${c.family_color || '#3B82F6'}15`
                                                                                        }}
                                                                                    >
                                                                                        {c.family_name}
                                                                                    </span>
                                                                                )}
                                                                                {isMax && <span className="px-1.5 py-0.5 rounded text-[8px] bg-emerald-500/20 text-emerald-300 font-bold uppercase tracking-wide">Max</span>}
                                                                                {isMin && <span className="px-1.5 py-0.5 rounded text-[8px] bg-red-500/20 text-red-300 font-bold uppercase tracking-wide">Min</span>}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-3 py-3 whitespace-nowrap">
                                                                            {c.label ? (
                                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase ${getLabelStyles(c.label)}`}>
                                                                                    {c.label}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-gray-600">-</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-3 py-3 text-right font-mono whitespace-nowrap">{c.trades_count}</td>
                                                                        <td className="px-3 py-3 text-right font-mono text-gray-300 whitespace-nowrap">
                                                                            {c.multiplier !== undefined && c.multiplier !== null && !isNaN(Number(c.multiplier)) ? (Number.isInteger(Number(c.multiplier)) ? Number(c.multiplier) : Number(c.multiplier).toFixed(2)) : '-'}
                                                                        </td>
                                                                        <td className="px-3 py-3 text-right font-mono text-blue-300 whitespace-nowrap">
                                                                            {c.deployed_capital !== undefined ? `₹${c.deployed_capital.toLocaleString('en-IN')}` : '-'}
                                                                        </td>
                                                                        <td className="px-3 py-3 text-right font-mono text-cyan-300 whitespace-nowrap">
                                                                            {c.available_funds !== undefined ? (c.available_funds < 0 ? '-' : '') + '₹' + Math.abs(c.available_funds).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                                                        </td>
                                                                        <td className="px-3 py-3 text-right font-mono text-purple-300 whitespace-nowrap">
                                                                            {c.delta_funds !== undefined ? (c.delta_funds < 0 ? '-' : '') + '₹' + Math.abs(c.delta_funds).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                                                        </td>
                                                                        <td className="px-3 py-3 text-right font-mono text-amber-300 whitespace-nowrap" title={`Ratio: Deployed / Available = ${c.ratio !== undefined && !isNaN(c.ratio) && isFinite(c.ratio) ? c.ratio.toFixed(2) : '-'}`}>
                                                                            {c.ratio !== undefined && c.ratio !== null && !isNaN(c.ratio) && isFinite(c.ratio) ? c.ratio.toFixed(2) : '-'}
                                                                        </td>
                                                                        <td className="px-3 py-3 text-center font-mono text-xs text-gray-400 border-r border-gray-800 whitespace-nowrap" title={c.balance_updated_at ? `Funds Updated: ${c.balance_updated_at}` : '-'}>
                                                                            {c.balance_updated_at ? FMT_DATETIME_SHORT(c.balance_updated_at) : '-'}
                                                                        </td>

                                                                        {/* Section 1: GodsEye System Performance (Net PnL Flow) */}
                                                                        <td className={`px-3 py-3 text-right font-mono font-medium whitespace-nowrap bg-blue-950/5 ${c.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                                            {c.total_pnl >= 0 ? '+' : ''}{c.total_pnl.toFixed(2)}
                                                                        </td>
                                                                        <td className="px-3 py-3 text-right font-mono text-amber-400 whitespace-nowrap bg-amber-950/15 border-l border-gray-800/40">
                                                                            <div className="flex items-center justify-end gap-1.5">
                                                                                <span>{c.total_charges !== undefined ? `₹${c.total_charges.toFixed(2)}` : '-'}</span>
                                                                                {c.charges_breakdown && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setChargesPopup(prev => prev?.id === c.client_id ? null : { type: 'client', id: c.client_id, client: c });
                                                                                        }}
                                                                                        className="opacity-60 hover:opacity-100 text-amber-400 hover:text-amber-200 p-0.5 rounded hover:bg-amber-400/20 transition-all cursor-pointer"
                                                                                        title={`Click to view charges breakdown for ${c.client_id}`}
                                                                                    >
                                                                                        <Info size={12} />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                        <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-blue-950/15 border-l border-gray-800/40 ${c.net_pnl !== undefined ? (c.net_pnl >= 0 ? 'text-emerald-300' : 'text-red-400') : 'text-gray-600'}`}>
                                                                            {c.net_pnl !== undefined ? `${c.net_pnl >= 0 ? '+' : ''}${c.net_pnl.toFixed(2)}` : '-'}
                                                                        </td>
                                                                        <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-blue-950/15 border-r border-gray-800/80 ${c.net_pnl_pct !== undefined ? (c.net_pnl_pct >= 0 ? 'text-emerald-300' : 'text-red-400') : 'text-gray-600'}`}>
                                                                            {c.net_pnl_pct !== undefined ? `${c.net_pnl_pct >= 0 ? '+' : ''}${c.net_pnl_pct.toFixed(2)}%` : '-'}
                                                                        </td>

                                                                        {/* Section 2: Broker Statement (CSV) */}
                                                                        {uploadedAuditCsv && (
                                                                            <>
                                                                                <td className={`px-3 py-3 text-right font-mono font-medium whitespace-nowrap bg-emerald-950/10 ${c.manual_pnl !== undefined ? (c.manual_pnl >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-600'}`}>
                                                                                    {c.manual_pnl !== undefined ? (c.manual_pnl >= 0 ? '+' : '') + c.manual_pnl.toFixed(2) : '-'}
                                                                                </td>
                                                                                <td className={`px-3 py-3 text-right font-mono font-medium whitespace-nowrap bg-emerald-950/10 border-r border-gray-800/80 ${c.manual_pnl_pct !== undefined ? (c.manual_pnl_pct >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-600'}`}>
                                                                                    {c.manual_pnl_pct !== undefined ? (c.manual_pnl_pct >= 0 ? '+' : '') + c.manual_pnl_pct.toFixed(2) + '%' : '-'}
                                                                                </td>
                                                                            </>
                                                                        )}

                                                                        {/* Section 3: Audit Variance */}
                                                                        {uploadedAuditCsv && (
                                                                            <>
                                                                                <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-amber-950/15 ${c.delta_pnl !== undefined ? (Math.abs(c.delta_pnl) < 1 ? 'text-emerald-400' : 'text-amber-400') : 'text-gray-600'}`} title={`Net PnL (₹${c.net_pnl !== undefined ? c.net_pnl.toFixed(2) : '-'}) - Broker PnL (₹${c.manual_pnl !== undefined ? c.manual_pnl.toFixed(2) : '-'})`}>
                                                                                    {c.delta_pnl !== undefined ? (c.delta_pnl > 0 ? '+' : '') + c.delta_pnl.toFixed(2) : '-'}
                                                                                </td>
                                                                                <td className={`px-3 py-3 text-right font-mono font-bold whitespace-nowrap bg-amber-950/15 ${c.delta_pnl_pct !== undefined ? (Math.abs(c.delta_pnl_pct) < 0.01 ? 'text-emerald-400' : 'text-amber-400') : 'text-gray-600'}`} title={`Net % (${c.net_pnl_pct !== undefined ? c.net_pnl_pct.toFixed(2) : '-'}%) - Broker % (${c.manual_pnl_pct !== undefined ? c.manual_pnl_pct.toFixed(2) : '-'}%)`}>
                                                                                    {c.delta_pnl_pct !== undefined ? (c.delta_pnl_pct > 0 ? '+' : '') + c.delta_pnl_pct.toFixed(2) + '%' : '-'}
                                                                                </td>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                );
                                                            })
                                                        )
                                                    )}
                                                </tbody>
                                                {clientTotals && (
                                                    <tfoot className="bg-[#0e1017] border-t-2 border-gray-700 font-bold text-xs sticky bottom-0 z-10 shadow-[0_-4px_10px_rgba(0,0,0,0.5)]">
                                                        <tr>
                                                            <td className="px-3 py-3 text-center text-gray-500 font-mono text-[10px] border-r border-gray-800">TOTAL</td>
                                                            <td colSpan={2} className="px-3 py-3 text-gray-300 font-bold uppercase tracking-wider text-[11px]">
                                                                {clientTotals.count} {clientTotals.count === 1 ? 'Client' : 'Clients'}
                                                            </td>
                                                            <td className="px-3 py-3 text-right font-mono font-bold text-gray-200">
                                                                {clientTotals.totalTrades > 0 ? clientTotals.totalTrades : '-'}
                                                            </td>
                                                            <td className="px-3 py-3 text-right font-mono font-bold text-cyan-300 whitespace-nowrap">
                                                                {clientTotals.totalMultiplier > 0 ? (Number.isInteger(clientTotals.totalMultiplier) ? clientTotals.totalMultiplier : clientTotals.totalMultiplier.toFixed(2)) : '-'}
                                                            </td>
                                                            <td className="px-3 py-3 text-right font-mono text-blue-300 whitespace-nowrap">
                                                                ₹{clientTotals.deployed.toLocaleString('en-IN')}
                                                            </td>
                                                            <td className="px-3 py-3 text-right font-mono text-cyan-300 whitespace-nowrap">
                                                                {(clientTotals.avail < 0 ? '-' : '') + '₹' + Math.abs(clientTotals.avail).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="px-3 py-3 text-right font-mono text-purple-300 whitespace-nowrap">
                                                                {(clientTotals.deltaFunds < 0 ? '-' : '') + '₹' + Math.abs(clientTotals.deltaFunds).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="px-3 py-3 text-right font-mono font-bold text-amber-300 whitespace-nowrap">
                                                                {clientTotals.ratio !== undefined && clientTotals.ratio !== null && !isNaN(clientTotals.ratio) && isFinite(clientTotals.ratio) ? clientTotals.ratio.toFixed(2) : '-'}
                                                            </td>
                                                            <td className="px-3 py-3 text-center font-mono font-normal text-xs text-gray-400 border-r border-gray-800 whitespace-nowrap" title={clientTotals.latestBalanceUpdated ? `Latest Funds Updated: ${clientTotals.latestBalanceUpdated}` : '-'}>
                                                                {clientTotals.latestBalanceUpdated ? FMT_DATETIME_SHORT(clientTotals.latestBalanceUpdated) : '-'}
                                                            </td>

                                                            {/* GodsEye System Totals (Net PnL Flow) */}
                                                            <td className={`px-3 py-3 text-right font-mono bg-[#101b33] whitespace-nowrap ${clientTotals.grossPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                                {clientTotals.grossPnl >= 0 ? '+' : ''}{clientTotals.grossPnl.toFixed(2)}
                                                            </td>
                                                            <td className="px-3 py-3 text-right font-mono text-amber-400 whitespace-nowrap bg-[#2a1708] border-l border-gray-800/60">
                                                                <div className="flex items-center justify-end gap-1.5">
                                                                    <span>₹{clientTotals.charges.toFixed(2)}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setChargesPopup(prev => prev?.type === 'totals' ? null : { type: 'totals', totals: clientTotals });
                                                                        }}
                                                                        className="p-1 rounded text-amber-400/80 hover:text-amber-200 hover:bg-amber-400/20 transition-all cursor-pointer"
                                                                        title="Click to view strategy total charges breakdown"
                                                                    >
                                                                        <Info size={12} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td className={`px-3 py-3 text-right font-mono font-extrabold whitespace-nowrap bg-[#152342] border-l border-gray-800/60 ${clientTotals.netPnl >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                                                                {clientTotals.netPnl >= 0 ? '+' : ''}{clientTotals.netPnl.toFixed(2)}
                                                            </td>
                                                            <td className={`px-3 py-3 text-right font-mono font-extrabold whitespace-nowrap bg-[#152342] border-r border-gray-800 ${clientTotals.netPct >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                                                                {clientTotals.netPct >= 0 ? '+' : ''}{clientTotals.netPct.toFixed(2)}%
                                                            </td>

                                                            {/* Broker CSV Totals */}
                                                            {uploadedAuditCsv && (
                                                                <>
                                                                    <td className={`px-3 py-3 text-right font-mono bg-[#0a271c] whitespace-nowrap ${clientTotals.brokerPnl !== undefined ? (clientTotals.brokerPnl >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-500'}`}>
                                                                        {clientTotals.brokerPnl !== undefined ? (clientTotals.brokerPnl >= 0 ? '+' : '') + clientTotals.brokerPnl.toFixed(2) : '-'}
                                                                    </td>
                                                                    <td className={`px-3 py-3 text-right font-mono bg-[#0a271c] border-r border-gray-800 whitespace-nowrap ${clientTotals.brokerPct !== undefined ? (clientTotals.brokerPct >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-500'}`}>
                                                                        {clientTotals.brokerPct !== undefined ? (clientTotals.brokerPct >= 0 ? '+' : '') + clientTotals.brokerPct.toFixed(2) + '%' : '-'}
                                                                    </td>
                                                                </>
                                                            )}

                                                            {/* Audit Variance Totals */}
                                                            {uploadedAuditCsv && (
                                                                <>
                                                                    <td className={`px-3 py-3 text-right font-mono font-extrabold whitespace-nowrap bg-[#281508] ${clientTotals.diffPnl !== undefined ? (Math.abs(clientTotals.diffPnl) < 1 ? 'text-emerald-400' : 'text-amber-400') : 'text-gray-500'}`}>
                                                                        {clientTotals.diffPnl !== undefined ? (clientTotals.diffPnl > 0 ? '+' : '') + clientTotals.diffPnl.toFixed(2) : '-'}
                                                                    </td>
                                                                    <td className={`px-3 py-3 text-right font-mono font-extrabold whitespace-nowrap bg-[#281508] ${clientTotals.diffPct !== undefined ? (Math.abs(clientTotals.diffPct) < 0.01 ? 'text-emerald-400' : 'text-amber-400') : 'text-gray-500'}`}>
                                                                        {clientTotals.diffPct !== undefined ? (clientTotals.diffPct > 0 ? '+' : '') + clientTotals.diffPct.toFixed(2) + '%' : '-'}
                                                                    </td>
                                                                </>
                                                            )}
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                )
                            ) : (
                                // Client Audit View
                                clientAuditLoading ? (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
                                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                                        <span className="text-sm">Matching live trades against backtest...</span>
                                    </div>
                                ) : clientAuditData.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                                        <AlertCircle className="w-8 h-8 mb-2 opacity-50 text-orange-400" />
                                        <span className="text-sm">No audit matching records generated.</span>
                                    </div>
                                ) : (
                                    <div className="flex-1 overflow-auto border border-gray-800 rounded-lg bg-[#14161f]/50 custom-scrollbar relative min-h-0">
                                        <table className="w-full text-left border-collapse text-[11px] min-w-[1200px]">
                                            <thead className="sticky top-0 z-20 shadow-md bg-[#14161f]">
                                                {/* Group Headers */}
                                                <tr className="bg-[#14161f] border-b border-gray-800 text-gray-400 uppercase font-bold text-[9px] text-center">
                                                    <th className="px-3 py-2 w-[40px] border-r border-gray-800 bg-[#14161f]">#</th>
                                                    <th className="px-3 py-2 text-blue-400 border-r border-gray-800 bg-[#14161f]" colSpan={6}>Live Data</th>
                                                    <th className="px-3 py-2 text-purple-400 border-r border-gray-800 bg-[#14161f]" colSpan={6}>Backtest Data</th>
                                                    <th className="px-3 py-2 text-amber-400 border-r border-gray-800 bg-[#14161f]" colSpan={3}>Charges & Net PnL</th>
                                                    <th className="px-3 py-2 text-center bg-[#14161f]" colSpan={4}>Status & Slippage</th>
                                                </tr>
                                                {/* Column Headers */}
                                                <tr className="bg-[#10121a] border-b border-gray-800 text-gray-400 font-semibold text-[10px]">
                                                    <th className="px-3 py-2 border-r border-gray-800 text-center bg-[#10121a]">#</th>
                                                    {/* Live */}
                                                    <th className="px-3 py-2">Symbol</th>
                                                    <th className="px-3 py-2">Strat</th>
                                                    <th className="px-3 py-2">Buy Time</th>
                                                    <th className="px-3 py-2 text-right">Buy Price</th>
                                                    <th className="px-3 py-2">Sell Time</th>
                                                    <th className="px-3 py-2 text-right border-r border-gray-800">Sell Price</th>
                                                    {/* Backtest */}
                                                    <th className="px-3 py-2">Symbol</th>
                                                    <th className="px-3 py-2">Strat</th>
                                                    <th className="px-3 py-2">Buy Time</th>
                                                    <th className="px-3 py-2 text-right">Buy Price</th>
                                                    <th className="px-3 py-2">Sell Time</th>
                                                    <th className="px-3 py-2 text-right border-r border-gray-800">Sell Price</th>
                                                    {/* Charges */}
                                                    <th className="px-3 py-2 text-right bg-amber-950/20 text-amber-500 border-r border-gray-800">Qty</th>
                                                    <th className="px-3 py-2 text-right bg-amber-950/20 text-amber-400 border-r border-gray-800" title="Brokerage + STT + Exchange + SEBI + Stamp + GST">Charges</th>
                                                    <th className="px-3 py-2 text-right bg-amber-950/20 text-amber-300 border-r border-gray-800">Net PnL</th>
                                                    {/* Stats */}
                                                    <th className="px-3 py-2 text-center">Status</th>
                                                    <th className="px-3 py-2">Comments</th>
                                                    <th className="px-3 py-2 text-right">Buy Slip</th>
                                                    <th className="px-3 py-2 text-right">Sell Slip</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                                {clientAuditData.map((row, index) => {
                                                    const isMatch = row.status === 'Match';
                                                    const isLiveOnly = !row.symbol_b;

                                                    let rowClass = "hover:bg-[#202330] transition-colors border-l-2 border-transparent";
                                                    if (isMatch) rowClass = "bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-100 border-l-2 border-emerald-500";
                                                    else if (isLiveOnly) rowClass = "bg-red-500/10 hover:bg-red-500/20 text-red-100 border-l-2 border-red-500";
                                                    else rowClass = "bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-100 border-l-2 border-yellow-500";

                                                    return (
                                                        <tr key={index} className={rowClass}>
                                                            <td className="px-3 py-2 text-center text-gray-500 border-r border-gray-800/40">{index + 1}</td>

                                                            {/* Live */}
                                                            <td className="px-3 py-2 font-medium">{row.symbol_l || '-'}</td>
                                                            <td className="px-3 py-2 text-blue-300/80">{row.strategy_tag_l || '-'}</td>
                                                            <td className="px-3 py-2 text-gray-400">
                                                                {FMT_DATETIME_SHORT(row.buy_time_l)}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-mono">
                                                                {row.buy_price_l !== null ? Number(row.buy_price_l).toFixed(2) : '-'}
                                                                {/* (Live Price) */}
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-400">
                                                                {FMT_DATETIME_SHORT(row.sell_time_l)}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-mono border-r border-gray-800/40">
                                                                {row.sell_price_l !== null ? Number(row.sell_price_l).toFixed(2) : '-'}
                                                            </td>

                                                            {/* Backtest */}
                                                            <td className="px-3 py-2 font-medium text-purple-200">{row.symbol_b || '-'}</td>
                                                            <td className="px-3 py-2 text-purple-300/60">{row.strategy_tag_b || '-'}</td>
                                                            <td className="px-3 py-2 text-gray-400">
                                                                {FMT_DATETIME_SHORT(row.buy_time_b)}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-mono">
                                                                {row.buy_price_b !== null ? Number(row.buy_price_b).toFixed(2) : '-'}
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-400">
                                                                {FMT_DATETIME_SHORT(row.sell_time_b)}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-mono border-r border-gray-800/40">
                                                                {row.sell_price_b !== null ? Number(row.sell_price_b).toFixed(2) : '-'}
                                                            </td>

                                                            {/* Stats */}
                                                            <td className="px-3 py-2 text-right font-mono bg-amber-950/10 text-amber-500 border-r border-gray-800/40">
                                                                {row.qty_l !== null && row.qty_l !== undefined ? row.qty_l : '-'}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-mono bg-amber-950/10 text-amber-400 border-r border-gray-800/40">
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <span>{row.charges ? `₹${row.charges.total_charges.toFixed(2)}` : '-'}</span>
                                                                    {row.charges && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setChargesPopup(prev => prev?.type === 'trade' && prev?.index === index + 1 ? null : { type: 'trade', index: index + 1, trade: row });
                                                                            }}
                                                                            className="opacity-50 hover:opacity-100 text-amber-400 hover:text-amber-200 p-0.5 rounded hover:bg-amber-400/20 transition-all cursor-pointer"
                                                                            title="Click to view trade charges breakdown"
                                                                        >
                                                                            <Info size={11} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className={`px-3 py-2 text-right font-mono font-semibold bg-amber-950/10 border-r border-gray-800/40 ${
                                                                row.net_pnl_l !== null && row.net_pnl_l !== undefined
                                                                    ? (row.net_pnl_l >= 0 ? 'text-emerald-300' : 'text-red-400')
                                                                    : 'text-gray-600'
                                                            }`}>
                                                                {row.net_pnl_l !== null && row.net_pnl_l !== undefined
                                                                    ? `${row.net_pnl_l >= 0 ? '+' : ''}${row.net_pnl_l.toFixed(2)}`
                                                                    : '-'}
                                                            </td>
                                                            <td className="px-3 py-2 text-center">
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border
                                                                    ${isMatch ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                                                        isLiveOnly ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                                                            'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'}`}>
                                                                    {row.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-400 max-w-[150px] truncate" title={row.comments}>{row.comments}</td>
                                                            <td className="px-3 py-2 text-right font-mono">
                                                                {row.slippage_buy !== null ? (
                                                                    <span className={Math.abs(Number(row.slippage_buy)) <= 0.01 ? "text-emerald-400" : "text-red-400"}>
                                                                        {(Number(row.slippage_buy) * 100).toFixed(2)}%
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-mono">
                                                                {row.slippage_sell !== null ? (
                                                                    <span className={Math.abs(Number(row.slippage_sell)) <= 0.01 ? "text-emerald-400" : "text-red-400"}>
                                                                        {(Number(row.slippage_sell) * 100).toFixed(2)}%
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Charges Info & Breakdown Modal (Click Only) */}
            {chargesPopup && (() => {
                const activeData = chargesPopup;
                const copyText = generateChargesCopyText(activeData);

                return (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-150"
                        onClick={() => setChargesPopup(null)}
                    >
                        <div
                            className="bg-[#151824] border border-amber-500/40 rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.85)] max-w-lg w-full overflow-hidden text-gray-200 select-text transition-all"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-3.5 bg-[#10121a] border-b border-gray-800">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">
                                        <Info size={16} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-xs text-gray-100 flex items-center gap-2">
                                            {activeData.type === 'rates' && "FYERS F&O Charges & Statutory Fee Schedule"}
                                            {activeData.type === 'client' && `Charges Breakdown — Client ${activeData.client?.client_id}`}
                                            {activeData.type === 'trade' && `Charges Breakdown — Trade #${activeData.index} (${activeData.trade?.symbol_l || '-'})`}
                                            {activeData.type === 'totals' && "Strategy Total Charges Breakdown"}
                                        </h3>
                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                            {activeData.type === 'rates' && "Official statutory rates & brokerage rules (Budget 2026 update)"}
                                            {activeData.type === 'client' && `${activeData.client?.name || 'Client'} • ${activeData.client?.trades_count || 0} Trades • Broker: ${activeData.client?.broker || 'Fyers'}`}
                                            {activeData.type === 'trade' && `Qty: ${activeData.trade?.qty_l || '-'} • Buy: ₹${activeData.trade?.buy_price_l || '-'} • Sell: ₹${activeData.trade?.sell_price_l || '-'}`}
                                            {activeData.type === 'totals' && `Aggregated across all ${activeData.totals?.count || 0} clients in this view`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleCopyCharges(copyText)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow transition-all cursor-pointer ${
                                            copiedState
                                                ? 'bg-emerald-500/25 text-emerald-300 border border-emerald-500/50'
                                                : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 hover:border-amber-500/60'
                                        }`}
                                        title="Copy charges breakdown to clipboard"
                                    >
                                        {copiedState ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                                        <span>{copiedState ? "Copied!" : "Copy List"}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setChargesPopup(null)}
                                        className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors cursor-pointer"
                                        title="Close"
                                    >
                                        <X size={15} />
                                    </button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-4 max-h-[65vh] overflow-y-auto space-y-3 custom-scrollbar">
                                {activeData.type === 'rates' && (
                                    <div className="space-y-2 text-xs">
                                        <div className="bg-[#1a1d2d] p-2.5 rounded-lg border border-gray-800/80 flex items-start justify-between">
                                            <div>
                                                <span className="font-semibold text-amber-300 block">1. Brokerage</span>
                                                <span className="text-gray-400 text-[11px]">Flat rate per executed order</span>
                                            </div>
                                            <span className="font-mono font-bold text-gray-200">₹20 / order (₹40 / trade)</span>
                                        </div>

                                        <div className="bg-[#1a1d2d] p-2.5 rounded-lg border border-gray-800/80 flex items-start justify-between">
                                            <div>
                                                <span className="font-semibold text-amber-300 block">2. STT / CTT</span>
                                                <span className="text-gray-400 text-[11px]">Securities / Commodities Transaction Tax on sell side</span>
                                            </div>
                                            <div className="text-right font-mono font-bold text-gray-200">
                                                <div>0.15% <span className="text-[10px] text-gray-400 font-normal">(Options Premium)</span></div>
                                                <div>0.05% <span className="text-[10px] text-gray-400 font-normal">(Futures Turnover)</span></div>
                                            </div>
                                        </div>

                                        <div className="bg-[#1a1d2d] p-2.5 rounded-lg border border-gray-800/80 flex items-start justify-between">
                                            <div>
                                                <span className="font-semibold text-amber-300 block">3. Transaction & Clearing Charges</span>
                                                <span className="text-gray-400 text-[11px]">Exchange + Clearing Corporation turnover fees</span>
                                            </div>
                                            <div className="text-right font-mono font-bold text-gray-200">
                                                <div>0.0445299% <span className="text-[10px] text-gray-400 font-normal">(NSE Options)</span></div>
                                                <div>0.0415% <span className="text-[10px] text-gray-400 font-normal">(BSE Options)</span></div>
                                                <div>0.0023299% <span className="text-[10px] text-gray-400 font-normal">(NSE Futures)</span></div>
                                            </div>
                                        </div>

                                        <div className="bg-[#1a1d2d] p-2.5 rounded-lg border border-gray-800/80 flex items-start justify-between">
                                            <div>
                                                <span className="font-semibold text-amber-300 block">4. SEBI Turnover Fee</span>
                                                <span className="text-gray-400 text-[11px]">₹10 per Crore on total turnover</span>
                                            </div>
                                            <span className="font-mono font-bold text-gray-200">0.000001 (₹10 / Cr)</span>
                                        </div>

                                        <div className="bg-[#1a1d2d] p-2.5 rounded-lg border border-gray-800/80 flex items-start justify-between">
                                            <div>
                                                <span className="font-semibold text-amber-300 block">5. Stamp Duty</span>
                                                <span className="text-gray-400 text-[11px]">State stamp duty applied on buy turnover</span>
                                            </div>
                                            <div className="text-right font-mono font-bold text-gray-200">
                                                <div>0.003% <span className="text-[10px] text-gray-400 font-normal">(Options Buy)</span></div>
                                                <div>0.002% <span className="text-[10px] text-gray-400 font-normal">(Futures Buy)</span></div>
                                            </div>
                                        </div>

                                        <div className="bg-[#1a1d2d] p-2.5 rounded-lg border border-gray-800/80 flex items-start justify-between">
                                            <div>
                                                <span className="font-semibold text-amber-300 block">6. GST</span>
                                                <span className="text-gray-400 text-[11px]">18% on (Brokerage + Exchange & Clearing + SEBI Fee)</span>
                                            </div>
                                            <span className="font-mono font-bold text-gray-200">18.00%</span>
                                        </div>
                                    </div>
                                )}

                                {(activeData.type === 'client' || activeData.type === 'totals' || activeData.type === 'trade') && (() => {
                                    const isClient = activeData.type === 'client';
                                    const isTrade = activeData.type === 'trade';
                                    const c = isClient ? activeData.client : null;
                                    const tr = isTrade ? activeData.trade : null;
                                    const b = isClient ? (c.charges_breakdown || {}) : isTrade ? (tr.charges || {}) : null;
                                    const t = (!isClient && !isTrade) ? activeData.totals : null;

                                    const brokerage = (isClient || isTrade) ? (b.brokerage || 0) : (t.totalBrokerage || 0);
                                    const stt = (isClient || isTrade) ? (b.stt || 0) : (t.totalStt || 0);
                                    const exchange = (isClient || isTrade) ? (b.exchange_charges || 0) : (t.totalExchange || 0);
                                    const sebi = (isClient || isTrade) ? (b.sebi_fee || 0) : (t.totalSebi || 0);
                                    const stamp = (isClient || isTrade) ? (b.stamp_duty || 0) : (t.totalStamp || 0);
                                    const gst = (isClient || isTrade) ? (b.gst || 0) : (t.totalGst || 0);
                                    const totalCharges = (isClient || isTrade) ? (b.total_charges || (c?.total_charges) || 0) : (t.charges || 0);
                                    const grossPnl = isClient ? (c.total_pnl || 0) : isTrade ? ((tr.sell_price_l - tr.buy_price_l) * (tr.qty_l || 1)) : (t.grossPnl || 0);
                                    const netPnl = isClient ? (c.net_pnl !== undefined ? c.net_pnl : grossPnl - totalCharges) : isTrade ? (tr.net_pnl_l || 0) : (t.netPnl || 0);
                                    const netPct = isClient ? (c.net_pnl_pct !== undefined ? c.net_pnl_pct : 0) : isTrade ? 0 : (t.netPct || 0);

                                    return (
                                        <div className="space-y-2.5 text-xs">
                                            <div className="grid grid-cols-2 gap-2 bg-[#1a1d2d] p-2.5 rounded-lg border border-gray-800">
                                                <div>
                                                    <span className="text-[10px] text-gray-400 uppercase tracking-wider block">Gross Trading PnL</span>
                                                    <span className={`font-mono font-bold text-sm ${grossPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                        {grossPnl >= 0 ? '+' : ''}₹{grossPnl.toFixed(2)}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-[10px] text-gray-400 uppercase tracking-wider block">Net Yield</span>
                                                    <span className={`font-mono font-bold text-sm ${netPnl >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                                                        {netPnl >= 0 ? '+' : ''}₹{netPnl.toFixed(2)} {netPct ? `(${netPct >= 0 ? '+' : ''}${netPct.toFixed(2)}%)` : ''}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="bg-[#10121a] rounded-lg border border-gray-800 overflow-hidden divide-y divide-gray-800/60 font-mono text-[11px]">
                                                <div className="flex justify-between items-center p-2 hover:bg-gray-800/30 transition-colors">
                                                    <span className="text-gray-400 font-sans">Brokerage {isClient && c.trades_count ? `(${b.orders_count || c.trades_count * 2} orders)` : ''}</span>
                                                    <span className="text-gray-100 font-bold">₹{brokerage.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 hover:bg-gray-800/30 transition-colors">
                                                    <span className="text-gray-400 font-sans">STT / CTT (0.15% Sell Premium)</span>
                                                    <span className="text-gray-100 font-bold">₹{stt.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 hover:bg-gray-800/30 transition-colors">
                                                    <span className="text-gray-400 font-sans">Exchange & Clearing Charges (0.0445%)</span>
                                                    <span className="text-gray-100 font-bold">₹{exchange.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 hover:bg-gray-800/30 transition-colors">
                                                    <span className="text-gray-400 font-sans">SEBI Turnover Fee (₹10 / Cr)</span>
                                                    <span className="text-gray-100 font-bold">₹{sebi.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 hover:bg-gray-800/30 transition-colors">
                                                    <span className="text-gray-400 font-sans">Stamp Duty (0.003% Buy Value)</span>
                                                    <span className="text-gray-100 font-bold">₹{stamp.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 hover:bg-gray-800/30 transition-colors">
                                                    <span className="text-gray-400 font-sans">GST (18% on Brokerage + Exch + SEBI)</span>
                                                    <span className="text-gray-100 font-bold">₹{gst.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2.5 bg-amber-500/10 text-amber-300 font-bold text-xs">
                                                    <span className="font-sans">Total Statutory & Broker Charges</span>
                                                    <span className="text-amber-300 text-sm">₹{totalCharges.toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Raw Selectable Text with 1-click focus */}
                                <div className="pt-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400">Raw Text (Select or Click to Copy)</span>
                                        <span className="text-[10px] text-gray-500">Clicking box auto-selects all</span>
                                    </div>
                                    <textarea
                                        readOnly
                                        value={copyText}
                                        rows={5}
                                        className="w-full bg-[#0d0f17] border border-gray-800 rounded-lg p-2.5 text-[11px] font-mono text-gray-300 focus:outline-none focus:border-amber-500/50 resize-none cursor-text select-all"
                                        onClick={e => e.target.select()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Family Group Configuration Modal */}
            <FamilyConfigModal
                isOpen={showFamilyModal}
                onClose={() => setShowFamilyModal(false)}
                allUsers={users}
                onFamilyUpdated={fetchFamilyGroups}
            />
        </div>
    );
};

export default PerformanceView;

